import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './CounterClose.css';
import fetchData from '../../functions/fetchData';
import fetchdatanotequal from '../../functions/viewAllData';
import CardComponent from '../../components/cards/CardComponent';
import FloatingNavModal from '../../components/floatingbuttons/FloatingNavModal';
import ReviewModalAntD from './ReviewModalAntD';
import { saveCashierSummary, checkCashierSummaryStatus } from '../../api/dayCloseApi';
import { CloseDateProvider } from '../../components/CloseDateProvider';
import { getHeaders } from '../../utility/getHeader';
import { 
  FaCashRegister,
  FaCreditCard, 
  FaChartPie,
  FaCalculator,
  FaFileInvoice,
  FaUser,
  FaSpinner,
  FaCheckCircle,
  FaTimes,
  FaDownload,
  FaEye
} from 'react-icons/fa';
import { Modal, Table, Tag, Card, Row, Col, Statistic, Divider, Button, Input, Spin, Alert, Badge, Typography, Space } from 'antd';
import { Modal as BootstrapModal } from 'react-bootstrap';
import { CheckCircleOutlined, CloseCircleOutlined, LockOutlined, UnlockOutlined, LoadingOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { ToastContainer, toast } from 'react-toastify';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend
} from 'chart.js';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend);

const CounterClose = ({ nextCloseDate, dateLoading, dateError }) => {
  const navigate = useNavigate();
  
  const [counterData, setCounterData] = useState({
    cashierInfo: {
      cashierId: '',
      cashierName: '',
      shift: 'Day Shift'
    },
    salesOverview: {
      totalSales: 0,
      totalTransactions: 0,
      averageTransaction: 0,
      totalDiscount: 0,
      netSales: 0
    },
    paymentBreakdown: {
      cash: { amount: 0, count: 0 },
      card: { amount: 0, count: 0 },
      btransfer: { amount: 0, count: 0 }
    },
    cashReconciliation: {
      expectedCash: 0,
      actualCash: 0,
      difference: 0,
      openingCash: 0,
      closingCash: 0
    },
    transactions: [],
    summary: {
      totalBills: 0,
      totalAmount: 0,
      totalPaid: 0,
      totalBalance: 0,
      pendingBills: 0
    }
  });

  const [loading, setLoading] = useState(false);
  const [isClosingCounter, setIsClosingCounter] = useState(false);
  const [actualCashInput, setActualCashInput] = useState('');
  const [notes, setNotes] = useState('');
  const [counterStatus, setCounterStatus] = useState('open'); // 'open', 'closing', 'closed'
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [expenseItems, setExpenseItems] = useState([]);
  const [currentExpense, setCurrentExpense] = useState({ item: '', amount: '' });
  const expenseItemInputRef = useRef(null);

  // Manual Money Report State
  const [moneyReport, setMoneyReport] = useState({
    denominations: {
      note1000: { qty: 0, total: 0 },
      note500: { qty: 0, total: 0 },
      note100: { qty: 0, total: 0 },
      note50: { qty: 0, total: 0 },
      note20: { qty: 0, total: 0 },
      note10: { qty: 0, total: 0 },
      note5: { qty: 0, total: 0 },
      note1: { qty: 0, total: 0 },
      photo: 0
    },
    coins: {
      coin10: { qty: 0, total: 0 },
      coin5: { qty: 0, total: 0 },
      coin2: { qty: 0, total: 0 },
      coin1: { qty: 0, total: 0 }
    },
    expenses: 0,
    cashOutCard: 0,
    cashOutTE: 0,
    amountReceivedToday: 0,
    cashInSystem: 0,
    creditCard: 0,
    transfer: 0,
    creditCardInSystem: 0,
    transferInSystem: 0,
    cardSettlement: '',
    pendingBills: 0
  });

  // Get user information
  const userType = (localStorage.getItem('usertype') || sessionStorage.getItem('usertype') || '').toLowerCase();
  const userId = localStorage.getItem('userid') || sessionStorage.getItem('userid');
  const userName = localStorage.getItem('uname') || sessionStorage.getItem('uname');
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  
  const isAuthenticated = !!token;
  const isCashier = userType === 'cashier' || userType === 'admin';

  // Fetch cashier details from users table
  const fetchCashierDetails = useCallback(async () => {
    try {
      if (!userId) return null;
      
      const whereCondition = `id=${userId}`;
      const userDetails = await fetchData('users', null, 'id', whereCondition);
      
      if (userDetails && userDetails.length > 0) {
        const user = userDetails[0];
        return {
          cashierId: user.uname || userId,
          cashierName: user.name || user.uname || `User ${userId}`,
          phone: user.phone || user.contact || '',
          role: user.type || userType,
          department: user.department || '',
          position: user.position || ''
        };
      }
      
      return {
        cashierId: userName || userId,
        cashierName: userName || `User ${userId}`,
        phone: '',
        role: userType,
        department: '',
        position: ''
      };
    } catch (error) {
      console.error('Error fetching cashier details:', error);
      return {
        cashierId: userId,
        cashierName: userName || `User ${userId}`,
        phone: '',
        role: userType,
        department: '',
        position: ''
      };
    }
  }, [userId, userName, userType]);

  // Fetch counter data for today
  const fetchCounterData = useCallback(async () => {
    setLoading(true);
    try {
      if (!userId) {
        toast.error('User authentication error. Please login again.');
        navigate('/');
        return;
      }

      // console.log('Fetching counter data for cashier:', userName, '(userid:', userId + ')');
      
      // Fetch cashier details from users table
      let cashierDetails;
      try {
        cashierDetails = await fetchCashierDetails();
      } catch (error) {
        console.error('Failed to fetch cashier details:', error);
        cashierDetails = {
          cashierId: userId,
          cashierName: userName || `User ${userId}`,
          phone: '',
          role: userType,
          department: '',
          position: ''
        };
      }
      
      // Get date from sale_date_setup table (use nextCloseDate from CloseDateProvider)
      const transactionDate = nextCloseDate || new Date().toISOString().split('T')[0];
      
      // Fetch paid transactions only (balance = 0) - no date filter
      const whereCondition = `balance=0`;
      const todayTransactions = await fetchData('collection', null, 'id DESC', whereCondition);
      
      // console.log('All PAID transactions:', todayTransactions);
      // console.log('Cashier:', userName);
      
      if (todayTransactions && todayTransactions.length > 0) {
        // Filter transactions for current user if cashier
        // NOTE: userid field in collection table stores cashier username (uname), not numeric user ID
        let userTransactions = todayTransactions;
        if (isCashier && userName) {
          // Use cashier username (uname) for filtering instead of numeric userId
          // since userid field in collection table stores cashier username
          userTransactions = todayTransactions.filter(transaction => 
            transaction.userid && transaction.userid.toString() === userName.toString()
          );
          
          // console.log('Filtering by cashier username:', userName);
          // console.log('All transactions count:', todayTransactions.length);
          // console.log('Filtered transactions for cashier:', userTransactions.length);
          
          // Log details of cashier transactions
          userTransactions.forEach(t => {
            // console.log(`Transaction ${t.id}: mode="${t.mode}", cash=${t.paidamount}, card=${t.card}, transfer=${t.btransfer}, date=${t.dte}, userid=${t.userid}`);
          });
        }
        
        // console.log('User transactions:', userTransactions);
        
        // Fetch pending bills separately - all transactions with balance > 0, no date or cashier filter
        const pendingBillsData = await fetchdatanotequal('collection', null, 'id', {balance: 0});
        const totalPendingBills = pendingBillsData.reduce((sum, transaction) => {
          const balance = parseFloat(transaction.balance) || 0; 
          return sum + balance;
        }, 0);
        // console.log('Pending bills data:', pendingBillsData?.length || 0, 'transactions, total:', totalPendingBills);
        
        // Calculate sales overview
        const salesData = userTransactions.reduce((acc, transaction) => {
          const netAmount = parseFloat(transaction.netamount) || 0;
          const discount = parseFloat(transaction.discount) || 0;
          const balance = parseFloat(transaction.balance) || 0;
          const mode = (transaction.mode || '').toLowerCase().trim();
          
          // Initialize payment amounts
          let cashAmount = 0;
          let cardAmount = 0;
          let transferAmount = 0;
          
          // Determine payment amounts based on mode
          if (mode === 'card' || mode.includes('card') || mode === 'credit card' || mode === 'creditcard') {
            // When mode is 'card', use paidamount as card payment
            cardAmount = parseFloat(transaction.paidamount) || 0;
            cashAmount = 0;
          } else if (mode === 'btransfer' || mode.includes('transfer') || mode === 'bank transfer') {
            // When mode is 'btransfer', use paidamount as transfer payment
            transferAmount = parseFloat(transaction.paidamount) || 0;
            cashAmount = 0;
          } else {
            // Default: treat as cash payment
            cashAmount = parseFloat(transaction.paidamount) || 0;
          }
          
          // Add any amounts from dedicated card/transfer columns (for mixed payments)
          cardAmount += parseFloat(transaction.card) || 0;
          transferAmount += parseFloat(transaction.btransfer) || 0;
          
          // Calculate total paid from all payment columns
          const totalPaidAmount = cashAmount + cardAmount + transferAmount;
          
          acc.totalSales += netAmount;
          acc.totalTransactions += 1;
          acc.totalDiscount += discount;
          acc.totalPaid += totalPaidAmount;
          acc.totalBalance += balance;
          
          // Payment breakdown - log for debugging
          // console.log('Transaction mode:', transaction.mode, '-> normalized:', mode, 'Cash:', cashAmount, 'Card:', cardAmount, 'Transfer:', transferAmount);
          
          // Payment breakdown with flexible Matching - use respective columns
          // For mixed payments, we need to add amounts from each payment method separately
          if (cashAmount > 0) {
            acc.paymentBreakdown.cash.amount += cashAmount;
            acc.paymentBreakdown.cash.count += 1;
          }
          if (cardAmount > 0) {
            acc.paymentBreakdown.card.amount += cardAmount;
            acc.paymentBreakdown.card.count += 1;
          }
          if (transferAmount > 0) {
            acc.paymentBreakdown.btransfer.amount += transferAmount;
            acc.paymentBreakdown.btransfer.count += 1;
          }
          
          return acc;
        }, {
          totalSales: 0,
          totalTransactions: 0,
          totalDiscount: 0,
          totalPaid: 0,
          totalBalance: 0,
          paymentBreakdown: {
            cash: { amount: 0, count: 0 },
            card: { amount: 0, count: 0 },
            btransfer: { amount: 0, count: 0 }
          }
        });
        
        // Calculate averages
        const averageTransaction = salesData.totalTransactions > 0 ? 
          salesData.totalSales / salesData.totalTransactions : 0;
        
        const netSales = salesData.totalSales - salesData.totalDiscount;
        
        // Log payment breakdown for debugging
        // console.log('Payment Breakdown Summary:');
        // console.log('Cash:', salesData.paymentBreakdown.cash);
        // console.log('Card:', salesData.paymentBreakdown.card);
        // console.log('Transfer:', salesData.paymentBreakdown.btransfer);
        
        // Get shift timing - removed openTime/closeTime as not needed
        
        setCounterData({
          cashierInfo: {
            cashierId: cashierDetails.cashierId,
            cashierName: cashierDetails.cashierName,
            phone: cashierDetails.phone,
            role: cashierDetails.role,
            department: cashierDetails.department,
            position: cashierDetails.position,
            shift: 'Day Shift'
          },
          salesOverview: {
            totalSales: salesData.totalSales,
            totalTransactions: salesData.totalTransactions,
            averageTransaction: averageTransaction,
            totalDiscount: salesData.totalDiscount,
            netSales: netSales
          },
          paymentBreakdown: salesData.paymentBreakdown,
          cashReconciliation: {
            expectedCash: salesData.paymentBreakdown.cash.amount,
            actualCash: 0,
            difference: 0,
            openingCash: 0,
            closingCash: 0
          },
          transactions: userTransactions,
          summary: {
            totalBills: salesData.totalTransactions,
            totalAmount: salesData.totalSales,
            totalPaid: salesData.totalPaid,
            totalBalance: salesData.totalBalance,
            pendingBills: totalPendingBills
          }
        });
        
        // console.log('Counter data calculated successfully');
        
      } else {
        // console.log('No transactions found for today');
        // Initialize with empty data
        const cashierDetails = await fetchCashierDetails();
        setCounterData(prev => ({
          ...prev,
          cashierInfo: {
            ...prev.cashierInfo,
            cashierId: cashierDetails.cashierId,
            cashierName: cashierDetails.cashierName,
            phone: cashierDetails.phone,
            role: cashierDetails.role,
            department: cashierDetails.department,
            position: cashierDetails.position
          }
        }));
      }
      
    } catch (error) {
      console.error('Error fetching counter data:', error);
      toast.error('Failed to fetch counter data');
    } finally {
      setLoading(false);
    }
  }, [userId, userName, userType, navigate, isCashier, fetchCashierDetails, nextCloseDate]);

  // Handle cash reconciliation input
  const handleCashInput = (value) => {
    setActualCashInput(value);
    const actualCash = parseFloat(value) || 0;
    const expectedCash = counterData.cashReconciliation.expectedCash;
    const difference = actualCash - expectedCash;
    
    setCounterData(prev => ({
      ...prev,
      cashReconciliation: {
        ...prev.cashReconciliation,
        actualCash: actualCash,
        difference: difference,
        closingCash: actualCash
      }
    }));
  };

  // Handle expense operations
  const handleAddExpenseRow = () => {
    if (currentExpense.item.trim() && currentExpense.amount) {
      const amount = parseFloat(currentExpense.amount);
      if (amount > 0) {
        const newExpense = {
          id: Date.now(),
          item: currentExpense.item.trim(),
          amount: amount
        };
        setExpenseItems([...expenseItems, newExpense]);
        setCurrentExpense({ item: '', amount: '' });
        
        // Update total expenses in moneyReport
        const totalExpenses = [...expenseItems, newExpense].reduce((sum, item) => sum + item.amount, 0);
        setMoneyReport(prev => ({...prev, expenses: totalExpenses}));
        
        // Focus on item details input for next entry
        setTimeout(() => {
          if (expenseItemInputRef.current) {
            expenseItemInputRef.current.focus();
          }
        }, 0);
      } else {
        toast.error('Amount must be greater than 0');
      }
    } else {
      toast.error('Please enter both item details and amount');
    }
  };

  const handleRemoveExpenseRow = (id) => {
    const updatedExpenses = expenseItems.filter(item => item.id !== id);
    setExpenseItems(updatedExpenses);
    
    // Update total expenses in moneyReport
    const totalExpenses = updatedExpenses.reduce((sum, item) => sum + item.amount, 0);
    setMoneyReport(prev => ({...prev, expenses: totalExpenses}));
  };

  const handleSaveExpenses = () => {
    setShowExpenseModal(false);
    toast.success(`${expenseItems.length} expense(s) saved successfully`);
  };

  // Close counter
  const handleCloseCounter = async () => {
    // Validate notes if there's a cash discrepancy
    if (counterData.cashReconciliation.difference !== 0 && !notes.trim()) {
      toast.error('Please provide notes explaining the cash discrepancy');
      return;
    }
    
    setIsClosingCounter(true);
    try {
      // Use close date from CloseDateProvider
      const closeDate = nextCloseDate || new Date().toISOString().split('T')[0];
      const cashierSummaryData = {
        cashier_id: counterData.cashierInfo.cashierId || userId,
        cashier_name: counterData.cashierInfo.cashierName || 'Unknown Cashier',
        date: closeDate,
        shift_type: 'Night Shift', // You can modify this based on your requirements
        open_time: counterData.cashierInfo.openTime ? new Date(counterData.cashierInfo.openTime).toTimeString().slice(0, 8) : null,
        close_time: new Date().toTimeString().slice(0, 8),
        total_sales: counterData.salesOverview.totalSales,
        total_transactions: counterData.salesOverview.totalTransactions,
        total_discount: counterData.salesOverview.totalDiscount,
        net_sales: counterData.salesOverview.netSales,
        cash_amount: counterData.paymentBreakdown.cash.amount,
        cash_count: counterData.paymentBreakdown.cash.count,
        card_amount: counterData.paymentBreakdown.card.amount,
        card_count: counterData.paymentBreakdown.card.count,
        transfer_amount: counterData.paymentBreakdown.btransfer.amount,
        transfer_count: counterData.paymentBreakdown.btransfer.count,
        expected_cash: counterData.cashReconciliation.expectedCash,
        actual_cash: parseFloat(actualCashInput) || 0,
        cash_difference: counterData.cashReconciliation.difference,
        opening_cash: 0, // Add if you track opening cash
        closing_cash: parseFloat(actualCashInput) || 0,
        counter_status: 'closed',
        
        // Currency Denominations
        note1000_qty: moneyReport.denominations.note1000?.qty || 0,
        note1000_total: moneyReport.denominations.note1000?.total || 0,
        note500_qty: moneyReport.denominations.note500?.qty || 0,
        note500_total: moneyReport.denominations.note500?.total || 0,
        note100_qty: moneyReport.denominations.note100?.qty || 0,
        note100_total: moneyReport.denominations.note100?.total || 0,
        note50_qty: moneyReport.denominations.note50?.qty || 0,
        note50_total: moneyReport.denominations.note50?.total || 0,
        note20_qty: moneyReport.denominations.note20?.qty || 0,
        note20_total: moneyReport.denominations.note20?.total || 0,
        note10_qty: moneyReport.denominations.note10?.qty || 0,
        note10_total: moneyReport.denominations.note10?.total || 0,
        note5_qty: moneyReport.denominations.note5?.qty || 0,
        note5_total: moneyReport.denominations.note5?.total || 0,
        note1_qty: moneyReport.denominations.note1?.qty || 0,
        note1_total: moneyReport.denominations.note1?.total || 0,
        photo_other: moneyReport.denominations.photo || 0,
        
        // Coin Denominations
        coin10_qty: moneyReport.coins.coin10?.qty || 0,
        coin10_total: moneyReport.coins.coin10?.total || 0,
        coin5_qty: moneyReport.coins.coin5?.qty || 0,
        coin5_total: moneyReport.coins.coin5?.total || 0,
        coin2_qty: moneyReport.coins.coin2?.qty || 0,
        coin2_total: moneyReport.coins.coin2?.total || 0,
        coin1_qty: moneyReport.coins.coin1?.qty || 0,
        coin1_total: moneyReport.coins.coin1?.total || 0,
        
        // Manual Payment Details
        manual_credit_card: moneyReport.creditCard || 0,
        manual_transfer: moneyReport.transfer || 0,
        card_settlement: moneyReport.cardSettlement || '',
        
        // Financial Details
        expenses: moneyReport.expenses || 0,
        cash_out_card: moneyReport.cashOutCard || 0,
        cash_out_transfer: moneyReport.cashOutTE || 0,
        amount_received_today: moneyReport.amountReceivedToday || 0,
        pending_bills: counterData.summary.pendingBills || 0
      };
      
      console.log('Saving cashier summary data:', cashierSummaryData);
      
      // Save to cashier_summaries table using API
      const saveResult = await saveCashierSummary(cashierSummaryData);
      
      if (!saveResult.success) {
        throw new Error(saveResult.message || 'Failed to save cashier summary');
      }
      
      // Save expense details to counter_expenses table
      if (expenseItems.length > 0) {
        try {
          const expensePromises = expenseItems.map(expense => {
            const expenseData = {
              cashier_summary_id: saveResult.summaryId || null,
              cashier_id: counterData.cashierInfo.cashierId || userId,
              cashier_name: counterData.cashierInfo.cashierName || userName || 'Unknown Cashier',
              date: closeDate,
              item_details: expense.item,
              amount: expense.amount
            };
            
            // Use axios.post to save each expense
            return axios.post('/insertData', {
              table: 'counter_expenses',
              data: expenseData
            }, getHeaders());
          });
          
          await Promise.all(expensePromises);
          console.log(`${expenseItems.length} expense items saved successfully`);
        } catch (expenseError) {
          console.error('Error saving expense details:', expenseError);
          toast.warning('Counter closed but some expense details may not have been saved');
        }
      }
      
      // Mark counter as closed in local storage to prevent re-login
      const counterCloseInfo = {
        date: closeDate,
        userId: userId,
        closedAt: new Date().toISOString(),
        status: 'closed'
      };
      localStorage.setItem('counterCloseStatus', JSON.stringify(counterCloseInfo));
      
      setCounterStatus('closed');
      toast.success('Counter closed successfully! Cashier summary completed.');
      
      // Generate closing report
      generateClosingReport();
      
    } catch (error) {
      console.error('Error closing counter:', error);
      toast.error('Failed to close counter. Please try again.');
    } finally {
      setIsClosingCounter(false);
    }
  };

  // Generate PDF closing report
  const generateClosingReport = () => {
    try {
      const doc = new jsPDF();
      
      // Colors for different sections
      const colors = {
        primary: [52, 152, 219],    // Blue
        success: [39, 174, 96],     // Green
        warning: [243, 156, 18],    // Orange
        danger: [231, 76, 60],      // Red
        dark: [52, 73, 94],         // Dark Gray
        light: [236, 240, 241]      // Light Gray
      };
      
      // Header with background
      doc.setFillColor(...colors.primary);
      doc.rect(0, 0, 210, 30, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(22);
      doc.setFont(undefined, 'bold');
      doc.text('COUNTER CLOSING REPORT', doc.internal.pageSize.getWidth() / 2, 20, { align: 'center' });
      
      // Reset text color
      doc.setTextColor(0, 0, 0);
      
      // Date and Status Bar
      doc.setFillColor(...colors.light);
      doc.rect(10, 35, 190, 15, 'F');
      doc.setDrawColor(...colors.dark);
      doc.rect(10, 35, 190, 15, 'S');
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.text(`Report Date: ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}`, 15, 45);
      const statusText = counterStatus === 'closed' ? 'STATUS: COUNTER CLOSED' : 'STATUS: COUNTER OPEN';
      doc.text(statusText, 140, 45);
      
      // Cashier Information Section
      doc.setFillColor(...colors.success);
      doc.rect(10, 55, 190, 8, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.text('CASHIER INFORMATION', 15, 61);
      
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(11);
      doc.setFont(undefined, 'normal');
      doc.text(`Cashier ID: ${counterData.cashierInfo.cashierId || userId}`, 15, 75);
      doc.text(`Cashier Name: ${counterData.cashierInfo.cashierName || userName || `User ${userId}`}`, 15, 85);
      doc.text(`Role: ${counterData.cashierInfo.role || userType}`, 15, 95);
      if (counterData.cashierInfo.department) {
        doc.text(`Department: ${counterData.cashierInfo.department}`, 110, 95);
      }
      
      // Sales Overview Section
      doc.setFillColor(...colors.primary);
      doc.rect(10, 105, 190, 8, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.text('SALES OVERVIEW', 15, 111);
      
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(11);
      doc.setFont(undefined, 'normal');
      
      // Sales boxes with borders
      const salesY = 125;
      const boxWidth = 45;
      const boxHeight = 20;
      
      // Total Sales Box
      doc.setFillColor(240, 255, 240);
      doc.rect(15, salesY, boxWidth, boxHeight, 'F');
      doc.setDrawColor(...colors.success);
      doc.rect(15, salesY, boxWidth, boxHeight, 'S');
      doc.setTextColor(...colors.success);
      doc.setFont(undefined, 'bold');
      doc.text('Total Sales', 17, salesY + 8);
      doc.setFontSize(10);
      doc.text(`฿${counterData.salesOverview.totalSales.toLocaleString()}`, 17, salesY + 16);
      
      // Transactions Box
      doc.setFillColor(240, 248, 255);
      doc.rect(65, salesY, boxWidth, boxHeight, 'F');
      doc.setDrawColor(...colors.primary);
      doc.rect(65, salesY, boxWidth, boxHeight, 'S');
      doc.setTextColor(...colors.primary);
      doc.setFont(undefined, 'bold');
      doc.setFontSize(11);
      doc.text('Transactions', 67, salesY + 8);
      doc.setFontSize(10);
      doc.text(`${counterData.salesOverview.totalTransactions}`, 67, salesY + 16);
      
      // Average Transaction Box
      doc.setFillColor(255, 248, 240);
      doc.rect(115, salesY, boxWidth, boxHeight, 'F');
      doc.setDrawColor(...colors.warning);
      doc.rect(115, salesY, boxWidth, boxHeight, 'S');
      doc.setTextColor(...colors.warning);
      doc.setFont(undefined, 'bold');
      doc.setFontSize(11);
      doc.text('Avg Transaction', 117, salesY + 8);
      doc.setFontSize(10);
      doc.text(`฿${counterData.salesOverview.averageTransaction.toFixed(0)}`, 117, salesY + 16);
      
      // Net Sales Box
      doc.setFillColor(248, 255, 248);
      doc.rect(165, salesY, boxWidth - 10, boxHeight, 'F');
      doc.setDrawColor(...colors.success);
      doc.rect(165, salesY, boxWidth - 10, boxHeight, 'S');
      doc.setTextColor(...colors.success);
      doc.setFont(undefined, 'bold');
      doc.setFontSize(11);
      doc.text('Net Sales', 167, salesY + 8);
      doc.setFontSize(10);
      doc.text(`฿${counterData.salesOverview.netSales.toLocaleString()}`, 167, salesY + 16);
      
      // Payment Breakdown Section
      doc.setFillColor(...colors.warning);
      doc.rect(10, 155, 190, 8, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.text('PAYMENT BREAKDOWN', 15, 161);
      
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(11);
      doc.setFont(undefined, 'normal');
      
      const paymentY = 170;
      doc.text(`Cash: Rs${counterData.paymentBreakdown.cash.amount.toLocaleString()} (${counterData.paymentBreakdown.cash.count} transactions)`, 15, paymentY);
      doc.text(`Card: Rs${counterData.paymentBreakdown.card.amount.toLocaleString()} (${counterData.paymentBreakdown.card.count} transactions)`, 15, paymentY + 10);
      doc.text(`Transfer: Rs${counterData.paymentBreakdown.btransfer.amount.toLocaleString()} (${counterData.paymentBreakdown.btransfer.count} transactions)`, 15, paymentY + 20);
      
      // Cash Reconciliation Section
      const reconcileColor = counterData.cashReconciliation.difference === 0 ? colors.success : colors.danger;
      doc.setFillColor(...reconcileColor);
      doc.rect(10, 200, 190, 8, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.text('CASH RECONCILIATION', 15, 206);
      
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(11);
      doc.setFont(undefined, 'normal');
      
      const reconY = 220;
      doc.text(`Expected Cash: ${counterData.cashReconciliation.expectedCash.toLocaleString()}`, 15, reconY);
      doc.text(`Actual Cash: ${counterData.cashReconciliation.actualCash.toLocaleString()}`, 15, reconY + 10);
      
      // Difference with color coding
      if (counterData.cashReconciliation.difference === 0) {
        doc.setTextColor(...colors.success);
        doc.setFont(undefined, 'bold');
        doc.text(`Difference: ${counterData.cashReconciliation.difference.toLocaleString()} - BALANCED`, 15, reconY + 20);
      } else {
        doc.setTextColor(...colors.danger);
        doc.setFont(undefined, 'bold');
        const status = counterData.cashReconciliation.difference > 0 ? 'EXCESS' : 'SHORTAGE';
        doc.text(`Difference: ${counterData.cashReconciliation.difference.toLocaleString()} - ${status}`, 15, reconY + 20);
      }
      
      // Notes (if any discrepancy)
      if (notes.trim()) {
        doc.setTextColor(0, 0, 0);
        doc.setFillColor(...colors.light);
        doc.rect(10, 250, 190, 20, 'F');
        doc.setDrawColor(...colors.danger);
        doc.rect(10, 250, 190, 20, 'S');
        doc.setFont(undefined, 'bold');
        doc.setFontSize(12);
        doc.text('DISCREPANCY NOTES', 15, 260);
        doc.setFont(undefined, 'normal');
        doc.setFontSize(10);
        const splitNotes = doc.splitTextToSize(notes, 180);
        doc.text(splitNotes, 15, 268);
      }
      
      // Footer
      const footerY = notes.trim() ? 285 : 255;
      doc.setFillColor(...colors.dark);
      doc.rect(10, footerY, 190, 25, 'F'); // Increased height from 15 to 25
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(10);
      doc.text(`Generated on: ${new Date().toLocaleString()}`, 15, footerY + 8);
      doc.text('Status: Counter Closed - Day End Complete', 15, footerY + 16); // Moved down from +12 to +16
      
      // Add bottom margin/spacing
      doc.setFontSize(8);
      doc.text('', 15, footerY + 22); // Add empty space at bottom
      
      // Save PDF
      const fileName = `Counter_Close_${counterData.cashierInfo.cashierId || userId}_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);
      
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error('Failed to generate report');
    }
  };

  // Export Review Details to PDF
  const exportReviewToPDF = () => {
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    let yPos = 12;

    // Header - Compact Light blue background
    pdf.setFillColor(230, 240, 255);
    pdf.rect(0, 0, pageWidth, 28, 'F');
    
    // Add logo (try to load it, if fails continue without it)
    try {
      const logoPath = '/logo.png'; // Adjust path as needed
      const logoImg = new Image();
      logoImg.src = logoPath;
      pdf.addImage(logoImg, 'PNG', 10, 5, 40, 15);
    } catch (error) {
      console.log('Logo not loaded:', error);
    }
    
    // Company/Title Header
    pdf.setTextColor(52, 73, 94);
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.text('COUNTER CLOSE REPORT', pageWidth / 2, 12, { align: 'center' });
    
    pdf.setFontSize(7);
    pdf.setFont('helvetica', 'normal');
    pdf.text(`Date: ${new Date().toLocaleDateString()} | Time: ${new Date().toLocaleTimeString()}`, pageWidth / 2, 18, { align: 'center' });
    
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'bold');
    pdf.text(`Cashier: ${counterData.cashierInfo.cashierName} (ID: ${counterData.cashierInfo.cashierId}) | ${counterData.cashierInfo.role}`, pageWidth / 2, 24, { align: 'center' });

    yPos = 34;

    // Data preparation
    const denominations = [
      { label: ' 1000', key: 'note1000' },
      { label: ' 500', key: 'note500' },
      { label: ' 100', key: 'note100' },
      { label: ' 50', key: 'note50' },
      { label: ' 20', key: 'note20' },
      { label: ' 10', key: 'note10' },
      { label: ' 5', key: 'note5' },
      { label: ' 1', key: 'note1' }
    ];

    const coins = [
      { label: ' 10', key: 'coin10' },
      { label: ' 5', key: 'coin5' },
      { label: ' 2', key: 'coin2' },
      { label: ' 1', key: 'coin1' }
    ];

    const totalCash = Object.entries(moneyReport.denominations)
      .reduce((sum, [key, value]) => {
        if (key === 'photo') return sum;
        return sum + (value.total || 0);
      }, 0);

    const totalCoins = Object.entries(moneyReport.coins)
      .reduce((sum, [key, value]) => {
        return sum + (value.total || 0);
      }, 0);

    // Combined Section Header
    pdf.setFillColor(245, 250, 255);
    pdf.rect(10, yPos - 5, pageWidth - 20, 7, 'F');
    pdf.setTextColor(41, 128, 185);
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Money Denominations', pageWidth / 2, yPos, { align: 'center' });
    yPos += 10;

    // Define column positions for side-by-side tables
    const leftTableX = 12;
    const rightTableX = pageWidth / 2 + 5;
    const colWidth = (pageWidth - 30) / 2;

    // Currency Denominations Table (Left Side)
    let currencyYPos = yPos;
    
    // Currency header
    pdf.setFillColor(232, 245, 233);
    pdf.rect(leftTableX, currencyYPos - 4, colWidth, 6, 'F');
    pdf.setTextColor(27, 94, 32);
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Currency Denominations', leftTableX + 2, currencyYPos);
    currencyYPos += 6;

    // Currency table header
    pdf.setFillColor(248, 249, 250);
    pdf.rect(leftTableX, currencyYPos, colWidth, 5, 'F');
    pdf.setTextColor(73, 80, 87);
    pdf.setFontSize(7);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Note', leftTableX + 3, currencyYPos + 3.5);
    pdf.text('Qty', leftTableX + colWidth * 0.45, currencyYPos + 3.5, { align: 'right' });
    pdf.text('Amount', leftTableX + colWidth - 3, currencyYPos + 3.5, { align: 'right' });
    currencyYPos += 6;
    
    // Currency rows
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(52, 58, 64);
    pdf.setFontSize(7);
    denominations.forEach((denom, index) => {
      const qty = moneyReport.denominations[denom.key]?.qty || 0;
      const total = moneyReport.denominations[denom.key]?.total || 0;
      
      if (index % 2 === 0) {
        pdf.setFillColor(252, 253, 254);
        pdf.rect(leftTableX, currencyYPos - 2.5, colWidth, 5, 'F');
      }
      
      pdf.text(denom.label, leftTableX + 3, currencyYPos);
      pdf.text(qty.toString(), leftTableX + colWidth * 0.45, currencyYPos, { align: 'right' });
      pdf.text(total.toLocaleString(), leftTableX + colWidth - 3, currencyYPos, { align: 'right' });
      currencyYPos += 5;
    });

    // Total Cash
    currencyYPos += 2;
    pdf.setFillColor(232, 245, 233);
    pdf.rect(leftTableX, currencyYPos - 2.5, colWidth, 6, 'F');
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(27, 94, 32);
    pdf.setFontSize(8);
    pdf.text('Total Cash', leftTableX + 3, currencyYPos);
    pdf.text(totalCash.toLocaleString(), leftTableX + colWidth - 3, currencyYPos, { align: 'right' });

    // Coin Denominations Table (Right Side)
    let coinYPos = yPos;
    
    // Coin header
    pdf.setFillColor(255, 245, 232);
    pdf.rect(rightTableX, coinYPos - 4, colWidth, 6, 'F');
    pdf.setTextColor(230, 126, 34);
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Coin Denominations', rightTableX + 2, coinYPos);
    coinYPos += 6;

    // Coin table header
    pdf.setFillColor(248, 249, 250);
    pdf.rect(rightTableX, coinYPos, colWidth, 5, 'F');
    pdf.setTextColor(73, 80, 87);
    pdf.setFontSize(7);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Coin', rightTableX + 3, coinYPos + 3.5);
    pdf.text('Qty', rightTableX + colWidth * 0.45, coinYPos + 3.5, { align: 'right' });
    pdf.text('Amount', rightTableX + colWidth - 3, coinYPos + 3.5, { align: 'right' });
    coinYPos += 6;
    
    // Coin rows
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(52, 58, 64);
    pdf.setFontSize(7);
    coins.forEach((coin, index) => {
      const qty = moneyReport.coins[coin.key]?.qty || 0;
      const total = moneyReport.coins[coin.key]?.total || 0;
      
      if (index % 2 === 0) {
        pdf.setFillColor(252, 253, 254);
        pdf.rect(rightTableX, coinYPos - 2.5, colWidth, 5, 'F');
      }
      
      pdf.text(coin.label, rightTableX + 3, coinYPos);
      pdf.text(qty.toString(), rightTableX + colWidth * 0.45, coinYPos, { align: 'right' });
      pdf.text(total.toLocaleString(), rightTableX + colWidth - 3, coinYPos, { align: 'right' });
      coinYPos += 5;
    });

    // Photo/Other (moved under coins)
    pdf.setFillColor(255, 248, 225);
    pdf.rect(rightTableX, coinYPos - 2.5, colWidth, 5, 'F');
    pdf.text('Photo', rightTableX + 3, coinYPos);
    pdf.text('-', rightTableX + colWidth * 0.45, coinYPos, { align: 'right' });
    pdf.text((moneyReport.denominations.photo || 0).toLocaleString(), rightTableX + colWidth - 3, coinYPos, { align: 'right' });
    coinYPos += 7;

    // Total Coins
    pdf.setFillColor(255, 245, 232);
    pdf.rect(rightTableX, coinYPos - 2.5, colWidth, 6, 'F');
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(230, 126, 34);
    pdf.setFontSize(8);
    pdf.text('Total Coins', rightTableX + 3, coinYPos);
    pdf.text(totalCoins.toLocaleString(), rightTableX + colWidth - 3, coinYPos, { align: 'right' });

    // Move yPos to the bottom of the taller table
    yPos = Math.max(currencyYPos, coinYPos) + 12;
    pdf.setTextColor(52, 58, 64);

    // Payment Methods Comparison Section (Compact) - MOVED ABOVE CASH SUMMARY
    pdf.setFillColor(245, 250, 255);
    pdf.rect(10, yPos - 4, pageWidth - 20, 6, 'F');
    pdf.setTextColor(41, 128, 185);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(9);
    pdf.text('Payment Methods - Manual vs System', 14, yPos);
    yPos += 8;

    // Table header (compact)
    pdf.setFillColor(248, 249, 250);
    pdf.rect(12, yPos, pageWidth - 24, 5, 'F');
    pdf.setTextColor(73, 80, 87);
    pdf.setFontSize(7);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Item', 15, yPos + 3.5);
    pdf.text('Manual', 60, yPos + 3.5, { align: 'right' });
    pdf.text('Expenses', 95, yPos + 3.5, { align: 'right' });
    pdf.text('System', 130, yPos + 3.5, { align: 'right' });
    pdf.text('Diff', 158, yPos + 3.5, { align: 'right' });
    pdf.text('Status', pageWidth - 15, yPos + 3.5, { align: 'right' });
    yPos += 11;
    
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(52, 58, 64);
    pdf.setFontSize(7);

    // Cash row (compact) - Manual + Expenses - System = Diff
    const cashExpenses = moneyReport.expenses || 0;
    const cashActualDiff = (totalCash + cashExpenses) - (counterData.paymentBreakdown.cash.amount || 0);
    const cashDiff = Math.abs(cashActualDiff);
    const cashMatch = cashDiff === 0;
    const cashIsShort = cashActualDiff < 0;
    pdf.setFillColor(252, 253, 254);
    pdf.rect(12, yPos - 2, pageWidth - 24, 5, 'F');
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(52, 58, 64);
    pdf.text('Cash', 15, yPos);
    pdf.text(totalCash.toLocaleString(), 60, yPos, { align: 'right' });
    pdf.text(cashExpenses.toLocaleString(), 95, yPos, { align: 'right' });
    pdf.text((counterData.paymentBreakdown.cash.amount || 0).toLocaleString(), 130, yPos, { align: 'right' });
    
    if (cashMatch) {
      pdf.setTextColor(46, 125, 50);
    } else if (cashIsShort) {
      pdf.setTextColor(211, 47, 47);
    } else {
      pdf.setTextColor(34, 139, 34);
    }
    pdf.text(cashMatch ? '0' : (cashIsShort ? `-${cashDiff.toLocaleString()}` : `+${cashDiff.toLocaleString()}`), 158, yPos, { align: 'right' });
    pdf.text(cashMatch ? 'Match' : (cashIsShort ? 'Short' : 'Excess'), pageWidth - 15, yPos, { align: 'right' });
    pdf.setTextColor(52, 58, 64);
    yPos += 5;

    // Card row (compact)
    const cardExpenses = moneyReport.cashOutCard || 0;
    const cardActualDiff = ((moneyReport.creditCard || 0) + cardExpenses) - (counterData.paymentBreakdown.card.amount || 0);
    const cardDiff = Math.abs(cardActualDiff);
    const cardMatch = cardDiff === 0;
    const cardIsShort = cardActualDiff < 0;
    pdf.setTextColor(52, 58, 64);
    pdf.text('Card', 15, yPos);
    pdf.text((moneyReport.creditCard || 0).toLocaleString(), 60, yPos, { align: 'right' });
    pdf.text(cardExpenses.toLocaleString(), 95, yPos, { align: 'right' });
    pdf.text((counterData.paymentBreakdown.card.amount || 0).toLocaleString(), 130, yPos, { align: 'right' });
    
    if (cardMatch) {
      pdf.setTextColor(46, 125, 50);
    } else if (cardIsShort) {
      pdf.setTextColor(211, 47, 47);
    } else {
      pdf.setTextColor(34, 139, 34);
    }
    pdf.text(cardMatch ? '0' : (cardIsShort ? `-${cardDiff.toLocaleString()}` : `+${cardDiff.toLocaleString()}`), 158, yPos, { align: 'right' });
    pdf.text(cardMatch ? 'Match' : (cardIsShort ? 'Short' : 'Excess'), pageWidth - 15, yPos, { align: 'right' });
    pdf.setTextColor(52, 58, 64);
    yPos += 5;

    // Transfer row (compact)
    const transferExpenses = moneyReport.cashOutTE || 0;
    const transferActualDiff = ((moneyReport.transfer || 0) + transferExpenses) - (counterData.paymentBreakdown.btransfer.amount || 0);
    const transferDiff = Math.abs(transferActualDiff);
    const transferMatch = transferDiff === 0;
    const transferIsShort = transferActualDiff < 0;
    pdf.setFillColor(252, 253, 254);
    pdf.rect(12, yPos - 2, pageWidth - 24, 5, 'F');
    pdf.setTextColor(52, 58, 64);
    pdf.text('Transfer', 15, yPos);
    pdf.text((moneyReport.transfer || 0).toLocaleString(), 60, yPos, { align: 'right' });
    pdf.text(transferExpenses.toLocaleString(), 95, yPos, { align: 'right' });
    pdf.text((counterData.paymentBreakdown.btransfer.amount || 0).toLocaleString(), 130, yPos, { align: 'right' });
    
    if (transferMatch) {
      pdf.setTextColor(46, 125, 50);
    } else if (transferIsShort) {
      pdf.setTextColor(211, 47, 47);
    } else {
      pdf.setTextColor(34, 139, 34);
    }
    pdf.text(transferMatch ? '0' : (transferIsShort ? `-${transferDiff.toLocaleString()}` : `+${transferDiff.toLocaleString()}`), 158, yPos, { align: 'right' });
    pdf.text(transferMatch ? 'Match' : (transferIsShort ? 'Short' : 'Excess'), pageWidth - 15, yPos, { align: 'right' });
    pdf.setTextColor(52, 58, 64);
    yPos += 6;

    // Grand Total row for Payment Methods
    const totalManual = totalCash + (moneyReport.creditCard || 0) + (moneyReport.transfer || 0);
    const totalExpenses = cashExpenses + cardExpenses + transferExpenses;
    const totalSystem = (counterData.paymentBreakdown.cash.amount || 0) + (counterData.paymentBreakdown.card.amount || 0) + (counterData.paymentBreakdown.btransfer.amount || 0);
    const totalDiffValue = (totalManual + totalExpenses) - totalSystem;
    const totalDiff = Math.abs(totalDiffValue);
    
    pdf.setFillColor(76, 175, 80);
    pdf.rect(12, yPos - 2, pageWidth - 24, 6, 'F');
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(8);
    pdf.setTextColor(255, 255, 255);
    pdf.text('GRAND TOTAL', 17, yPos);
    pdf.setFontSize(7);
    pdf.text(totalManual.toLocaleString(), 60, yPos, { align: 'right' });
    pdf.text(totalExpenses.toLocaleString(), 95, yPos, { align: 'right' });
    pdf.text(totalSystem.toLocaleString(), 130, yPos, { align: 'right' });
    pdf.text(totalDiff.toLocaleString(), 158, yPos, { align: 'right' });
    pdf.text('TOTAL', pageWidth - 15, yPos, { align: 'right' });
    pdf.setTextColor(52, 58, 64);
    pdf.setFontSize(7);
    pdf.setFont('helvetica', 'normal');
    yPos += 10;

    // Cash Summary and Financial Summary - TWO COLUMNS IN ONE ROW
    const summaryStartY = yPos;
    const leftColX = 12;
    const rightColX = pageWidth / 2 + 5;
    const colWidthHalf = (pageWidth - 30) / 2;

    // LEFT COLUMN: Cash Summary Section (Compact)
    let leftYPos = summaryStartY;
    pdf.setFillColor(240, 248, 255);
    pdf.rect(leftColX, leftYPos - 4, colWidthHalf, 6, 'F');
    pdf.setTextColor(25, 118, 210);
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Cash Summary', leftColX + 2, leftYPos);
    leftYPos += 8;

    // Summary table header (compact)
    pdf.setFillColor(248, 249, 250);
    pdf.rect(leftColX, leftYPos, colWidthHalf, 5, 'F');
    pdf.setTextColor(73, 80, 87);
    pdf.setFontSize(7);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Description', leftColX + 3, leftYPos + 3.5);
    pdf.text('Amount', leftColX + colWidthHalf - 3, leftYPos + 3.5, { align: 'right' });
    leftYPos += 7;
    
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(52, 58, 64);

    // Total Cash (Manual)
    pdf.setFillColor(232, 245, 233);
    pdf.rect(leftColX, leftYPos - 2, colWidthHalf, 5, 'F');
    pdf.text('Total Cash (Manual)', leftColX + 3, leftYPos);
    pdf.text(totalCash.toLocaleString(), leftColX + colWidthHalf - 3, leftYPos, { align: 'right' });
    leftYPos += 5;

    // Total Coins
    pdf.setFillColor(255, 245, 232);
    pdf.rect(leftColX, leftYPos - 2, colWidthHalf, 5, 'F');
    pdf.text('Total Coins', leftColX + 3, leftYPos);
    pdf.text(totalCoins.toLocaleString(), leftColX + colWidthHalf - 3, leftYPos, { align: 'right' });
    leftYPos += 5;

    // Photo/Other
    pdf.setFillColor(255, 248, 225);
    pdf.rect(leftColX, leftYPos - 2, colWidthHalf, 5, 'F');
    pdf.text('Photo/Other', leftColX + 3, leftYPos);
    pdf.text((moneyReport.denominations.photo || 0).toLocaleString(), leftColX + colWidthHalf - 3, leftYPos, { align: 'right' });
    leftYPos += 8;

    // Grand Total - compact and highlighted
    const grandTotal = totalCash + totalCoins + (moneyReport.denominations.photo || 0);
    pdf.setFillColor(76, 175, 80);
    pdf.rect(leftColX, leftYPos - 2, colWidthHalf, 6, 'F');
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(8);
    pdf.setTextColor(255, 255, 255);
    pdf.text('GRAND TOTAL', leftColX + 3, leftYPos);
    pdf.text(grandTotal.toLocaleString(), leftColX + colWidthHalf - 3, leftYPos, { align: 'right' });
    pdf.setTextColor(52, 58, 64);
    pdf.setFontSize(7);
    leftYPos += 8;

    // RIGHT COLUMN: Financial Summary Section (Compact)
    let rightYPos = summaryStartY;
    pdf.setFillColor(245, 250, 255);
    pdf.rect(rightColX, rightYPos - 4, colWidthHalf, 6, 'F');
    pdf.setTextColor(41, 128, 185);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(9);
    pdf.text('Financial Summary', rightColX + 2, rightYPos);
    rightYPos += 8;
    
    pdf.setFontSize(7);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(52, 58, 64);
    
    const financialItems = [
      { label: 'Expenses', value: moneyReport.expenses || 0 },
      { label: 'Cash Out - Card', value: moneyReport.cashOutCard || 0 },
      { label: 'Cash Out - Transfer', value: moneyReport.cashOutTE || 0 },
      { label: 'Amount Received Today', value: moneyReport.amountReceivedToday || 0 },
      { label: 'Total Coins Amount', value: Object.entries(moneyReport.coins).reduce((sum, [key, value]) => sum + (value.total || 0), 0), highlight: 'green' },
      { label: 'Total Manual Cash', value: Object.entries(moneyReport.denominations).reduce((sum, [key, value]) => { if (key === 'photo') return sum; return sum + (value.total || 0); }, 0), highlight: 'green' },
      { label: 'Photo/Other', value: moneyReport.denominations.photo || 0, highlight: 'green' },
      { label: 'GRAND TOTAL', value: Object.entries(moneyReport.coins).reduce((sum, [key, value]) => sum + (value.total || 0), 0) + Object.entries(moneyReport.denominations).reduce((sum, [key, value]) => { if (key === 'photo') return sum; return sum + (value.total || 0); }, 0) + (moneyReport.denominations.photo || 0), highlight: 'grandTotal' },
      { label: 'Pending Bills', value: counterData.summary.pendingBills || 0, highlight: 'orange' }
    ];
    
    financialItems.forEach((item, index) => {
      if (item.highlight === 'grandTotal') {
        pdf.setFillColor(76, 175, 80);
        pdf.rect(rightColX, rightYPos - 2, colWidthHalf, 6, 'F');
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(255, 255, 255);
        pdf.text(item.label, rightColX + 3, rightYPos);
        pdf.text(item.value.toLocaleString(), rightColX + colWidthHalf - 3, rightYPos, { align: 'right' });
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(52, 58, 64);
        rightYPos += 6;
      } else if (item.highlight === 'green') {
        pdf.setFillColor(232, 245, 233);
        pdf.rect(rightColX, rightYPos - 2, colWidthHalf, 5, 'F');
        pdf.text(item.label, rightColX + 3, rightYPos);
        pdf.text(item.value.toLocaleString(), rightColX + colWidthHalf - 3, rightYPos, { align: 'right' });
      } else if (item.highlight === 'orange') {
        pdf.setFillColor(255, 243, 224);
        pdf.rect(rightColX, rightYPos - 2, colWidthHalf, 5, 'F');
        pdf.text(item.label, rightColX + 3, rightYPos);
        pdf.text(item.value.toLocaleString(), rightColX + colWidthHalf - 3, rightYPos, { align: 'right' });
      } else if (index % 2 === 0) {
        pdf.setFillColor(252, 253, 254);
        pdf.rect(rightColX, rightYPos - 2, colWidthHalf, 5, 'F');
        pdf.text(item.label, rightColX + 3, rightYPos);
        pdf.text(item.value.toLocaleString(), rightColX + colWidthHalf - 3, rightYPos, { align: 'right' });
      } else {
        pdf.text(item.label, rightColX + 3, rightYPos);
        pdf.text(item.value.toLocaleString(), rightColX + colWidthHalf - 3, rightYPos, { align: 'right' });
      }
      rightYPos += 5;
    });

    // Move yPos to bottom of taller column
    yPos = Math.max(leftYPos, rightYPos) + 3;

    // Grand Totals Section (Compact)
    pdf.setFillColor(245, 250, 255);
    pdf.rect(10, yPos - 4, pageWidth - 20, 6, 'F');
    pdf.setTextColor(41, 128, 185);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(9);
    pdf.text('Grand Totals', 14, yPos);
    yPos += 9;
    
    const manualCashCount = Object.entries(moneyReport.denominations)
      .reduce((sum, [key, value]) => {
        if (key === 'photo') return sum;
        return sum + (value.total || 0);
      }, 0);
    
    const totalCoinsAmount = Object.entries(moneyReport.coins)
      .reduce((sum, [key, value]) => {
        return sum + (value.total || 0);
      }, 0);
    
    const grandTotalAll = totalCoinsAmount + manualCashCount + (moneyReport.denominations.photo || 0);
    
    // Manual Cash Count - compact box
    pdf.setFillColor(232, 245, 233);
    pdf.roundedRect(12, yPos - 3, (pageWidth - 28) / 2, 8, 1, 1, 'F');
    pdf.setTextColor(27, 94, 32);
    pdf.setFontSize(7);
    pdf.text('Manual Cash Count', 15, yPos);
    pdf.setFontSize(9);
    pdf.text(manualCashCount.toLocaleString(), 15, yPos + 4);
    
    // Grand Total - compact box
    pdf.setFillColor(76, 175, 80);
    pdf.roundedRect((pageWidth / 2) + 2, yPos - 3, (pageWidth - 28) / 2, 8, 1, 1, 'F');
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(7);
    pdf.text('Grand Total (Cash+Coins+Photo)', (pageWidth / 2) + 5, yPos);
    pdf.setFontSize(9);
    pdf.text(grandTotalAll.toLocaleString(), (pageWidth / 2) + 5, yPos + 4);
    yPos += 10;

    // Expense Details Section - Show all expense items in FOUR COLUMNS
    if (expenseItems && expenseItems.length > 0) {
      pdf.setFillColor(255, 243, 224);
      pdf.rect(10, yPos - 4, pageWidth - 20, 6, 'F');
      pdf.setTextColor(230, 126, 34);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(9);
      pdf.text('Expense Details', 14, yPos);
      yPos += 8;
      
      // Define four column positions
      const col1X = 12;
      const col2X = pageWidth / 4 + 5;
      const col3X = (pageWidth / 4) * 2;
      const col4X = (pageWidth / 4) * 3 - 5;
      const colWidth = (pageWidth - 24) / 4;
      
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(52, 58, 64);
      pdf.setFontSize(7);
      
      // Track positions for each column
      let col1Y = yPos;
      let col2Y = yPos;
      let col3Y = yPos;
      let col4Y = yPos;
      
      let expenseTotal = 0;
      let columnIndex = 0;
      
      expenseItems.forEach((expense, index) => {
        expenseTotal += expense.amount;
        
        // Determine which column to use
        let currentX, currentY;
        if (columnIndex === 0) {
          currentX = col1X;
          currentY = col1Y;
        } else if (columnIndex === 1) {
          currentX = col2X;
          currentY = col2Y;
        } else if (columnIndex === 2) {
          currentX = col3X;
          currentY = col3Y;
        } else {
          currentX = col4X;
          currentY = col4Y;
        }
        
        // Alternate background for readability
        if (index % 2 === 0) {
          pdf.setFillColor(252, 253, 254);
          pdf.rect(currentX, currentY - 2, colWidth, 6, 'F');
        }
        
        // Truncate item name to fit in column and display with amount on same line
        const itemName = expense.item.length > 15 ? expense.item.substring(0, 12) + '...' : expense.item;
        pdf.setFont('helvetica', 'normal');
        pdf.text(`${itemName} - ${expense.amount.toLocaleString()}`, currentX + 2, currentY);
        
        // Update column position
        if (columnIndex === 0) {
          col1Y += 6;
        } else if (columnIndex === 1) {
          col2Y += 6;
        } else if (columnIndex === 2) {
          col3Y += 6;
        } else {
          col4Y += 6;
        }
        
        // Move to next column
        columnIndex = (columnIndex + 1) % 4;
      });
      
      // Find the tallest column to set yPos
      yPos = Math.max(col1Y, col2Y, col3Y, col4Y) + 2;
      
      // Expense Grand Total (full width)
      pdf.setFillColor(230, 126, 34);
      pdf.rect(12, yPos, pageWidth - 24, 6, 'F');
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(8);
      pdf.setTextColor(255, 255, 255);
      pdf.text('TOTAL EXPENSES', 15, yPos + 4);
      pdf.text(`${expenseTotal.toLocaleString()}`, pageWidth - 15, yPos + 4, { align: 'right' });
      pdf.setTextColor(52, 58, 64);
      pdf.setFontSize(7);
      pdf.setFont('helvetica', 'normal');
      yPos += 10;
    }

    // Footer
    pdf.setFontSize(6);
    pdf.setFont('helvetica', 'italic');
    pdf.setTextColor(149, 165, 166);
    pdf.text('System-generated report | Please verify all entries before counter closing', pageWidth / 2, pageHeight - 10, { align: 'center' });

    // Save PDF
    pdf.save(`Counter_Close_Review_${new Date().toISOString().split('T')[0]}_${counterData.cashierInfo.cashierName}.pdf`);
    toast.success('Review report exported to PDF successfully!');
  };

  // Generate Thermal Print report
  const generateThermalPrint = () => {
    try {
      // Create thermal print content following BarcodePayment styling
      const thermalContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Counter Close Report - Thermal Print</title>
        <style>
          @media print {
            @page { margin: 0; size: 80mm auto; }
            body { margin: 0; }
          }
          body {
            font-family: monospace;
            font-size: 11px;
            line-height: 1.2;
            margin: 0;
            padding: 5mm;
            width: 70mm;
          }
          .center { text-align: center; }
          .bold { font-weight: bold; }
          .divider { border-bottom: 1px dashed #000; margin: 5px 0; }
          .row { display: flex; justify-content: space-between; }
          .small { font-size: 9px; }
          .large { font-size: 13px; }
          .section-header {
            background: #f0f0f0;
            padding: 2px;
            margin: 5px 0;
            text-align: center;
            font-weight: bold;
          }
        </style>
      </head>
      <body>
        <div class="center bold large">JANNAAT CLUB</div>
        <div class="center small">Counter Close Report</div>
        <div class="divider"></div>
        
        <div class="section-header">CASHIER INFORMATION</div>
        <div class="row">
          <span>Cashier ID:</span>
          <span>${counterData.cashierInfo.cashierId || userId}</span>
        </div>
        <div class="row">
          <span>Cashier:</span>
          <span>${counterData.cashierInfo.cashierName || userName || `User ${userId}`}</span>
        </div>
        <div class="row">
          <span>Date:</span>
          <span>${new Date().toLocaleDateString()}</span>
        </div>
        <div class="row">
          <span>Time:</span>
          <span>${new Date().toLocaleTimeString()}</span>
        </div>
        
        <div class="divider"></div>
        <div class="section-header">SALES SUMMARY</div>
        
        <div class="row bold">
          <span>Total Sales:</span>
          <span>฿${counterData.salesOverview.totalSales.toLocaleString()}</span>
        </div>
        <div class="row">
          <span>Transactions:</span>
          <span>${counterData.salesOverview.totalTransactions}</span>
        </div>
        <div class="row">
          <span>Avg Transaction:</span>
          <span>฿${counterData.salesOverview.averageTransaction.toFixed(2)}</span>
        </div>
        <div class="row">
          <span>Discount:</span>
          <span>฿${counterData.salesOverview.totalDiscount.toLocaleString()}</span>
        </div>
        <div class="row bold">
          <span>Net Sales:</span>
          <span>฿${counterData.salesOverview.netSales.toLocaleString()}</span>
        </div>
        
        <div class="divider"></div>
        <div class="section-header">PAYMENT BREAKDOWN</div>
        
        <div style="margin-bottom: 8px;">
          <div class="bold">CASH PAYMENTS</div>
          <div class="row">
            <span>Amount:</span>
            <span>฿${counterData.paymentBreakdown.cash.amount.toLocaleString()}</span>
          </div>
          <div class="row small">
            <span>Count:</span>
            <span>${counterData.paymentBreakdown.cash.count} transactions</span>
          </div>
          <div style="border-bottom: 1px dotted #ccc; margin: 3px 0;"></div>
        </div>
        
        <div style="margin-bottom: 8px;">
          <div class="bold">CARD PAYMENTS</div>
          <div class="row">
            <span>Amount:</span>
            <span>฿${counterData.paymentBreakdown.card.amount.toLocaleString()}</span>
          </div>
          <div class="row small">
            <span>Count:</span>
            <span>${counterData.paymentBreakdown.card.count} transactions</span>
          </div>
          <div style="border-bottom: 1px dotted #ccc; margin: 3px 0;"></div>
        </div>
        
        <div style="margin-bottom: 8px;">
          <div class="bold">BANK TRANSFERS</div>
          <div class="row">
            <span>Amount:</span>
            <span>฿${counterData.paymentBreakdown.btransfer.amount.toLocaleString()}</span>
          </div>
          <div class="row small">
            <span>Count:</span>
            <span>${counterData.paymentBreakdown.btransfer.count} transactions</span>
          </div>
          <div style="border-bottom: 1px dotted #ccc; margin: 3px 0;"></div>
        </div>
        
        ${false && `
        <div class="divider"></div>
        <div class="section-header">CASH RECONCILIATION</div>
        
        <div class="row">
          <span>Expected Cash:</span>
          <span>฿${counterData.cashReconciliation.expectedCash.toLocaleString()}</span>
        </div>
        <div class="row">
          <span>Actual Cash:</span>
          <span>฿${counterData.cashReconciliation.actualCash.toLocaleString()}</span>
        </div>
        <div class="row ${counterData.cashReconciliation.difference === 0 ? 'bold' : 'bold'}">
          <span>Difference:</span>
          <span>฿${counterData.cashReconciliation.difference.toLocaleString()}</span>
        </div>
        
        ${counterData.cashReconciliation.difference !== 0 ? `
          <div class="center bold" style="margin: 5px 0; padding: 2px; background: #ffeeee;">
            *** ALERT ***<br>
            ${counterData.cashReconciliation.difference > 0 ? 'EXCESS' : 'SHORTAGE'}: 
            ฿${Math.abs(counterData.cashReconciliation.difference).toLocaleString()}<br>
            *** ALERT ***
          </div>
        ` : `
          <div class="center bold" style="margin: 5px 0; padding: 2px; background: #eeffee;">
            � CASH BALANCED �
          </div>
        `}
        `}
        
        ${notes.trim() ? `
          <div class="divider"></div>
          <div class="section-header">DISCREPANCY NOTES</div>
          <div style="padding: 3px; background: #f9f9f9; font-size: 10px;">
            ${notes}
          </div>
        ` : ''}
        
        <div class="divider"></div>
        <div class="center bold">STATUS: COUNTER CLOSED</div>
        <div class="divider"></div>
        
        <div class="center small">
          Report ID: CC-${userId}-${new Date().toISOString().split('T')[0]}<br>
          Generated: ${new Date().toLocaleString()}
        </div>
      </body>
      </html>
    `;
      
      // Create a new window for thermal print with BarcodePayment style
      const printWindow = window.open('', '_blank', 'width=800,height=800,scrollbars=yes,resizable=yes');
      printWindow.document.write(thermalContent);
      printWindow.document.close();
      
      // Print and automatically close the dialog
      printWindow.print();
      
      // Close the print preview window after a short delay
      setTimeout(() => {
        printWindow.close();
      }, 1000);
      
      toast.success('Counter close thermal print initiated');
      
    } catch (error) {
      console.error('Error generating thermal print:', error);
      toast.error('Failed to generate thermal print');
    }
  };

  // Check if counter is already closed for today
  const checkCounterStatus = useCallback(async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      
      // Use API to check day close status
      const cashierStatus = await checkCashierSummaryStatus(today, userId);
      
      if (cashierStatus.isClosed) {
        setCounterStatus('closed');
        toast.warning('Counter has already been closed for today.');
        return true;
      }
      
      // Also check local storage as fallback
      const counterCloseInfo = JSON.parse(localStorage.getItem('counterCloseStatus') || '{}');
      
      if (counterCloseInfo.date === today && 
          counterCloseInfo.userId === userId && 
          counterCloseInfo.status === 'closed') {
        setCounterStatus('closed');
        toast.warning('Counter has already been closed for today.');
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Error checking counter status:', error);
      return false;
    }
  }, [userId]);

  useEffect(() => {
    const initializeCounterClose = async () => {
      if (!isAuthenticated) {
        navigate('/login');
        return;
      }

      if (!isCashier) {
        toast.error('Access denied. Only cashiers can access this page.');
        navigate('/dashboard/admin');
        return;
      }

      // Check if counter is already closed
      const alreadyClosed = await checkCounterStatus();
      
      // Fetch counter data only if not already closed
      if (!alreadyClosed) {
        fetchCounterData();
      }
    };
    
    initializeCounterClose();
  }, [isAuthenticated, isCashier, navigate, fetchCounterData, checkCounterStatus]);

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="counter-close">
      <ToastContainer />
      
      <div style={{padding: '24px'}}>
        {/* Header */}
        <Row gutter={[16, 16]} style={{marginBottom: '24px'}}>
          <Col span={24}>
            <Card style={{backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '8px'}}>
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'start'}}>
                <div style={{flexGrow: 1}}>
                  <Title level={2} style={{marginBottom: '4px'}}>
                    <FaCashRegister style={{marginRight: '8px', color: '#ffc107'}} />
                    Counter Close
                  </Title>
                  <Text type="secondary">
                    Daily counter reconciliation and closing
                  </Text>
                </div>
                <div style={{minWidth: '280px'}}>
                  <Badge 
                    status={counterStatus === 'open' ? 'success' : counterStatus === 'closing' ? 'warning' : 'error'}
                    text={
                      <span style={{fontSize: '0.95rem', fontWeight: 500}}>
                        {counterStatus === 'open' ? <UnlockOutlined style={{marginRight: '8px'}} /> : 
                         counterStatus === 'closing' ? <LoadingOutlined style={{marginRight: '8px'}} /> : 
                         <LockOutlined style={{marginRight: '8px'}} />}
                        {counterStatus === 'open' ? 'Counter Open' : 
                         counterStatus === 'closing' ? 'Closing in Progress' : 'Counter Closed'}
                      </span>
                    }
                    style=
                      {{padding: '8px 16px', 
                      backgroundColor: counterStatus === 'open' ? '#d4edda' : counterStatus === 'closing' ? '#fff3cd' : '#f8d7da',
                      borderRadius: '4px',
                      display: 'block',
                      textAlign: 'center'}}
                  />
                </div>
              </div>
            </Card>
          </Col>
        </Row>

        {loading ? (
          <div style={{textAlign: 'center', padding: '80px 0'}}>
            <Spin size="large" tip="Loading counter data..." />
          </div>
        ) : (
          <>
            {/* Cashier Info */}
            <Row gutter={[16, 16]} style={{marginBottom: '24px'}}>
              <Col md={12} xs={24}>
                <Card 
                  style={{backgroundColor: '#e3f2fd', border: '1px solid #2196f3', borderRadius: '8px'}}
                  title={
                    <span style={{color: 'white'}}>
                      <FaUser style={{marginRight: '8px'}} />
                      Cashier Information
                    </span>
                  }
                  headStyle={{backgroundColor: '#2196f3', borderRadius: '8px 8px 0 0'}}
                >
                  <Row gutter={[16, 8]}>
                    <Col span={12}>
                      <Paragraph><Text strong>Cashier ID:</Text> {counterData.cashierInfo.cashierId}</Paragraph>
                      <Paragraph><Text strong>Name:</Text> {counterData.cashierInfo.cashierName}</Paragraph>
                      {counterData.cashierInfo.department && (
                        <Paragraph><Text strong>Department:</Text> {counterData.cashierInfo.department}</Paragraph>
                      )}
                    </Col>
                    <Col span={12}>
                      <Paragraph><Text strong>Date:</Text> {new Date().toLocaleDateString()}</Paragraph>
                      <Paragraph><Text strong>Role:</Text> {counterData.cashierInfo.role}</Paragraph>
                      {counterData.cashierInfo.position && (
                        <Paragraph><Text strong>Position:</Text> {counterData.cashierInfo.position}</Paragraph>
                      )}
                    </Col>
                  </Row>
                </Card>
              </Col>
              
              <Col md={12} xs={24}>
                <Card 
                  style={{backgroundColor: '#e8f5e9', border: '1px solid #4caf50', borderRadius: '8px'}}
                  title={
                    <span style={{color: 'white'}}>
                      <FaFileInvoice style={{marginRight: '8px'}} />
                      Billing Summary
                    </span>
                  }
                  headStyle={{backgroundColor: '#4caf50', borderRadius: '8px 8px 0 0'}}
                >
                  <Row gutter={[16, 8]}>
                    <Col span={12}>
                      <Paragraph><Text strong>Total Bills:</Text> {counterData.summary.totalBills}</Paragraph>
                      <Paragraph><Text strong>Total Amount:</Text> ฿{counterData.summary.totalAmount.toLocaleString()}</Paragraph>
                    </Col>
                    <Col span={12}>
                      <Paragraph><Text strong>Total Paid:</Text> ฿{counterData.summary.totalPaid.toLocaleString()}</Paragraph>
                      <Paragraph><Text strong>Balance:</Text> ฿{counterData.summary.totalBalance.toLocaleString()}</Paragraph>
                    </Col>
                  </Row>
                </Card>
              </Col>
            </Row>

            {/* Sales Overview */}
            <div className="sales-overview-section">
              <h4 className="sales-overview-title">
                <FaChartPie className="me-2" />
                Sales Overview
              </h4>
              <div className="sales-overview-grid">
                <div className="modern-summary-card card-green">
                  <div className="card-icon">💵</div>
                  <div className="card-value">฿{counterData.salesOverview.totalSales.toLocaleString()}</div>
                  <div className="card-label">Total Sales</div>
                </div>
                
                <div className="modern-summary-card card-purple">
                  <div className="card-icon">📊</div>
                  <div className="card-value">{counterData.salesOverview.totalTransactions}</div>
                  <div className="card-label">Transactions</div>
                </div>
                
                <div className="modern-summary-card card-pink">
                  <div className="card-icon">📈</div>
                  <div className="card-value">฿{counterData.salesOverview.averageTransaction.toFixed(0)}</div>
                  <div className="card-label">Avg Transaction</div>
                </div>
                
                <div className="modern-summary-card card-orange">
                  <div className="card-icon">🎫</div>
                  <div className="card-value">฿{counterData.salesOverview.totalDiscount.toLocaleString()}</div>
                  <div className="card-label">Total Discount</div>
                </div>
                
                <div className="modern-summary-card card-cyan">
                  <div className="card-icon">💎</div>
                  <div className="card-value">฿{counterData.salesOverview.netSales.toLocaleString()}</div>
                  <div className="card-label">Net Sales</div>
                </div>
              </div>
            </div>

            {/* Manual Money Report Section */}
            <div className="row mb-4">
              <div className="col-12">
                <CardComponent className="money-report-wrapper">
                  <h5 className="money-report-header">
                    <FaCalculator className="me-2" />
                    Manual Money Report
                  </h5>
                  
                  <div className="money-report-content">
                    {/* Currency and Coin Denominations in Two Columns */}
                    <div className="row">
                      {/* Currency Denominations - Left Column */}
                      <div className="col-lg-6 mb-4">
                        <Card 
                          className="denomination-card currency-card"
                          title={
                            <span className="denomination-card-title">
                              💵 Currency Denominations
                            </span>
                          }
                          bordered={true}
                        >
                          <div className="table-responsive">
                            <table className="table table-sm denomination-table-inner">
                              <thead>
                                <tr>
                                  <th>Denomination</th>
                                  <th>Qty</th>
                                  <th>Amount</th>
                                  <th>Total</th>
                                </tr>
                              </thead>
                              <tbody>
                                {[
                                  { label: '1000', value: 1000, key: 'note1000' },
                                  { label: '500', value: 500, key: 'note500' },
                                  { label: '100', value: 100, key: 'note100' },
                                  { label: '50', value: 50, key: 'note50' },
                                  { label: '20', value: 20, key: 'note20' },
                                  { label: '10', value: 10, key: 'note10' },
                                  { label: '5', value: 5, key: 'note5' },
                                  { label: '1', value: 1, key: 'note1' }
                                ].map((denom) => (
                                  <tr key={denom.key}>
                                    <td className="denomination-label">฿{denom.label}</td>
                                    <td>
                                      <input
                                        type="number"
                                        className="form-control form-control-sm denomination-input"
                                        placeholder="0"
                                        value={moneyReport.denominations[denom.key].qty || ''}
                                        onChange={(e) => {
                                          const qty = parseInt(e.target.value) || 0;
                                          const total = qty * denom.value;
                                          setMoneyReport(prev => ({
                                            ...prev,
                                            denominations: {
                                              ...prev.denominations,
                                              [denom.key]: { qty, total }
                                            }
                                          }));
                                        }}
                                      />
                                    </td>
                                    <td className="text-muted">× {denom.value}</td>
                                    <td className="denomination-total">฿{moneyReport.denominations[denom.key].total.toLocaleString()}</td>
                                  </tr>
                                ))}
                                <tr className="photo-row">
                                  <td className="denomination-label">Photo/Other</td>
                                  <td colSpan="2">
                                    <input
                                      type="number"
                                      className="form-control form-control-sm denomination-input"
                                      placeholder="0"
                                      value={moneyReport.denominations.photo || ''}
                                      onChange={(e) => {
                                        const value = parseFloat(e.target.value) || 0;
                                        setMoneyReport(prev => ({
                                          ...prev,
                                          denominations: { ...prev.denominations, photo: value }
                                        }));
                                      }}
                                    />
                                  </td>
                                  <td className="denomination-total">฿{(moneyReport.denominations.photo || 0).toLocaleString()}</td>
                                </tr>
                                <tr className="total-row">
                                  <td colSpan="3" className="fw-bold">Total Currency</td>
                                  <td className="fw-bold total-amount">
                                    ฿{(
                                      Object.entries(moneyReport.denominations)
                                        .reduce((sum, [key, value]) => {
                                          if (key === 'photo') return sum;
                                          return sum + (value.total || 0);
                                        }, 0)
                                    ).toLocaleString()}
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </Card>
                      </div>

                      {/* Coin Denominations - Right Column */}
                      <div className="col-lg-6 mb-4">
                        <Card 
                          className="denomination-card coin-card"
                          title={
                            <span className="denomination-card-title">
                              🪙 Coin Denominations
                            </span>
                          }
                          bordered={true}
                        >
                          <div className="table-responsive">
                            <table className="table table-sm denomination-table-inner">
                              <thead>
                                <tr>
                                  <th>Denomination</th>
                                  <th>Qty</th>
                                  <th>Amount</th>
                                  <th>Total</th>
                                </tr>
                              </thead>
                              <tbody>
                                {[
                                  { label: '10', value: 10, key: 'coin10' },
                                  { label: '5', value: 5, key: 'coin5' },
                                  { label: '2', value: 2, key: 'coin2' },
                                  { label: '1', value: 1, key: 'coin1' }
                                ].map((coin) => (
                                  <tr key={coin.key}>
                                    <td className="denomination-label">฿{coin.label}</td>
                                    <td>
                                      <input
                                        type="number"
                                        className="form-control form-control-sm denomination-input"
                                        placeholder="0"
                                        value={moneyReport.coins[coin.key].qty || ''}
                                        onChange={(e) => {
                                          const qty = parseInt(e.target.value) || 0;
                                          const total = qty * coin.value;
                                          setMoneyReport(prev => ({
                                            ...prev,
                                            coins: {
                                              ...prev.coins,
                                              [coin.key]: { qty, total }
                                            }
                                          }));
                                        }}
                                      />
                                    </td>
                                    <td className="text-muted">× {coin.value}</td>
                                    <td className="denomination-total">฿{moneyReport.coins[coin.key].total.toLocaleString()}</td>
                                  </tr>
                                ))}
                                {/* Empty rows to match currency table height */}
                                {[...Array(5)].map((_, idx) => (
                                  <tr key={`empty-${idx}`} className="empty-row">
                                    <td colSpan="4" className="border-0"></td>
                                  </tr>
                                ))}
                                <tr className="total-row">
                                  <td colSpan="3" className="fw-bold">Total Coins</td>
                                  <td className="fw-bold total-amount">
                                    ฿{(
                                      Object.values(moneyReport.coins)
                                        .reduce((sum, coin) => sum + (coin.total || 0), 0)
                                    ).toLocaleString()}
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </Card>
                      </div>
                    </div>

                    {/* Financial Summary */}
                    <h6 className="financial-summary-title">💰 Financial Summary</h6>
                    <div className="row mb-4">
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Expenses</label>
                        <div className="d-flex gap-2 mb-2">
                          <input
                            type="text"
                            className="form-control"
                            value={`฿${expenseItems.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0).toLocaleString()}`}
                            readOnly
                            style={{background: '#fff3cd', fontWeight: '600', color: '#856404'}}
                          />
                          <Button 
                            type="primary" 
                            onClick={() => setShowExpenseModal(true)}
                            style={{backgroundColor: '#ff9800', borderColor: '#ff9800'}}
                          >
                            + Add Expense
                          </Button>
                        </div>
                        {expenseItems.length > 0 && (
                          <div style={{fontSize: '12px', color: '#666'}}>
                            {expenseItems.length} expense item(s)
                          </div>
                        )}
                      </div>
                      <div className="col-md-3 col-sm-6 mb-3">
                        <label className="form-label">Cash Out - CARD</label>
                        <input
                          type="number"
                          className="form-control"
                          placeholder="Amount"
                          value={moneyReport.cashOutCard || ''}
                          onChange={(e) => setMoneyReport(prev => ({...prev, cashOutCard: parseFloat(e.target.value) || 0}))}
                        />
                      </div>
                      <div className="col-md-3 col-sm-6 mb-3">
                        <label className="form-label">Cash Out - Transfer</label>
                        <input
                          type="number"
                          className="form-control"
                          placeholder="Amount"
                          value={moneyReport.cashOutTE || ''}
                          onChange={(e) => setMoneyReport(prev => ({...prev, cashOutTE: parseFloat(e.target.value) || 0}))}
                        />
                      </div>
                    </div>

                    {/* Amount Received & System */}
                    <h6 className="mb-3" style={{color: '#667eea', fontWeight: '600'}}>🔄 System Reconciliation</h6>
                    <div className="row mb-4">
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Amount Received Today</label>
                        <input
                          type="number"
                          className="form-control"
                          placeholder="Amount received"
                          value={moneyReport.amountReceivedToday || ''}
                          onChange={(e) => setMoneyReport(prev => ({...prev, amountReceivedToday: parseFloat(e.target.value) || 0}))}
                        />
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label">
                          Cash in System 
                          <span className="badge bg-info ms-2">Auto-calculated</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={`฿${(counterData.paymentBreakdown.cash.amount || 0).toLocaleString()}`}
                          readOnly
                          style={{
                            background: '#e3f2fd',
                            fontWeight: '700',
                            color: '#1976d2'
                          }}
                        />
                      </div>
                    </div>

                    {/* Payment Methods */}
                    <Card 
                      className="payment-methods-card"
                      title={
                        <span className="payment-methods-title">
                          💳 Payment Methods
                        </span>
                      }
                      bordered={true}
                    >
                      <div className="row g-3">
                        <div className="col-md-3 col-sm-6">
                          <label className="form-label financial-input-label">Credit Card</label>
                          <input
                            type="number"
                            className="form-control payment-method-input"
                            placeholder="Amount"
                            value={moneyReport.creditCard || ''}
                            onChange={(e) => setMoneyReport(prev => ({...prev, creditCard: parseFloat(e.target.value) || 0}))}
                          />
                        </div>
                        <div className="col-md-3 col-sm-6">
                          <label className="form-label financial-input-label">Transfer</label>
                          <input
                            type="number"
                            className="form-control payment-method-input"
                            placeholder="Amount"
                            value={moneyReport.transfer || ''}  
                            onChange={(e) => setMoneyReport(prev => ({...prev, transfer: parseFloat(e.target.value) || 0}))}
                          />
                        </div>
                        <div className="col-md-3 col-sm-6">
                          <label className="form-label financial-input-label">
                            Credit Card (In System)
                            <span className="badge bg-info auto-calculated-badge">Auto-calculated</span>
                          </label>
                          <input
                            type="text"
                            className="form-control auto-calculated-input"
                            value={`฿${(counterData.paymentBreakdown.card.amount || 0).toLocaleString()}`}
                            readOnly
                          />
                        </div>
                        <div className="col-md-3 col-sm-6">
                          <label className="form-label" style={{fontWeight: '600', fontSize: '0.9rem'}}>
                            Transfer (In System)
                            <span className="badge bg-info ms-2" style={{fontSize: '0.7rem'}}>Auto-calculated</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            value={`฿${(counterData.paymentBreakdown.btransfer.amount || 0).toLocaleString()}`}
                            readOnly
                            style={{
                              background: '#e0f2f1',
                              fontWeight: '700',
                              fontSize: '1.1rem',
                              color: '#00796b',
                              border: '2px solid #17a2b8',
                              cursor: 'not-allowed'
                            }}
                          />
                        </div>
                      </div>
                    </Card>

                    {/* Card Settlement & Pending Bills */}
                    <Card 
                      className="mb-3"
                      title={
                        <span style={{color: '#f08934', fontWeight: '600', fontSize: '1.1rem'}}>
                          ℹ️ Additional Information
                        </span>
                      }
                      bordered={true}
                      style={{
                        borderColor: '#ff9800',
                        borderWidth: '2px',
                        boxShadow: '0 4px 12px rgba(255, 152, 0, 0.15)'
                      }}
                      headStyle={{
                        backgroundColor: '#fff3e0',
                        borderBottom: '2px solid #ff9800'
                      }}
                    >
                      <div className="row g-3">
                        <div className="col-md-6">
                          <label className="form-label" style={{fontWeight: '600', fontSize: '0.9rem'}}>Card Settlement</label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Settlement details"
                            value={moneyReport.cardSettlement}
                            onChange={(e) => setMoneyReport(prev => ({...prev, cardSettlement: e.target.value}))}
                          />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label" style={{fontWeight: '600', fontSize: '0.9rem'}}>
                            Pending Bills in System
                            <span className="badge bg-warning ms-2" style={{fontSize: '0.7rem'}}>Balance &gt; 0</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            value={`฿${(counterData.summary.pendingBills || 0).toLocaleString()}`}
                            readOnly
                            style={{
                              background: '#fff3e0',
                              fontWeight: '700',
                              fontSize: '1.1rem',
                              color: '#e65100',
                              border: '2px solid #ff9800',
                              cursor: 'not-allowed',
                              textAlign: 'center'
                            }}
                          />
                        </div>
                      </div>
                    </Card>

                    {/* Summary Display */}
                    <Card
                      title={
                        <span style={{fontWeight: '600', fontSize: '1.1rem'}}>
                          📊 Summary Totals
                        </span>
                      }
                      bordered={true}
                      style={{
                        borderColor: '#795548',
                        borderWidth: '2px',
                        boxShadow: '0 4px 12px rgba(121, 85, 72, 0.15)'
                      }}
                      headStyle={{
                        background: 'linear-gradient(135deg, #F8E9BFFF 0%, #EAF0D4FF 100%)',
                        borderBottom: '2px solid #795548'
                      }}
                      bodyStyle={{
                      background: 'linear-gradient(135deg, #F8E9BFFF 0%, #EAF0D4FF 100%)',
                      borderRadius: '8px',
                      padding: '15px',
                      color: 'black',
                      marginTop: '20px'
                    }}>
                      <div className="row g-2">
                        <div className="col-md-4">
                          <div className="text-center">
                            <small>Total Cash Count</small>
                            <div style={{fontSize: '1.2rem', fontWeight: '700'}}>
                              ฿{(
                                Object.entries(moneyReport.denominations)
                                  .reduce((sum, [key, value]) => {
                                    if (key === 'photo') return sum; // Skip photo, don't add it
                                    return sum + (value.total || 0);
                                  }, 0)
                              ).toLocaleString()}
                            </div>
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="text-center">
                            <small>Total Payments</small>
                            <div style={{fontSize: '1.2rem', fontWeight: '700'}}>
                              ฿{(moneyReport.creditCard + moneyReport.transfer).toLocaleString()}
                            </div>
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="text-center">
                            <small>Grand Total</small>
                            <div style={{fontSize: '1.2rem', fontWeight: '700'}}>
                              ฿{(
                                Object.entries(moneyReport.coins)
                                  .reduce((sum, [key, value]) => {
                                    return sum + (value.total || 0);
                                  }, 0) +
                                Object.entries(moneyReport.denominations)
                                  .reduce((sum, [key, value]) => {
                                    if (key === 'photo') return sum;
                                    return sum + (value.total || 0);
                                  }, 0) +
                                (moneyReport.denominations.photo || 0)
                              ).toLocaleString()}
                            </div>
                          </div>
                        </div>
                      </div>
                    </Card>
                  </div>
                </CardComponent>
              </div>
            </div>

         

            {/* Action Buttons */}
            {counterStatus === 'open' && (
              <Row gutter={[16, 16]} style={{marginTop: '20px'}}>
                <Col span={24}>
                  <Card 
                  
                  >
                    <Space size="large" wrap style={{width: '100%', justifyContent: 'center'}}>
                      <Button 
                        type="primary"
                        size="large"
                        icon={<FaEye style={{fontSize: '18px'}} />}
                        onClick={() => setShowReviewModal(true)}
                        style={{
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          borderColor: '#667eea',
                          height: '50px',
                          minWidth: '180px',
                          fontSize: '15px',
                          fontWeight: '600',
                          borderRadius: '8px',
                          boxShadow: '0 4px 12px rgba(102, 126, 234, 0.5)',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                        onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                      >
                        REVIEW DETAILS
                      </Button>
                      <Button 
                        danger
                        type="primary"
                        size="large"
                        icon={isClosingCounter ? <LoadingOutlined style={{fontSize: '18px'}} /> : <FaTimes style={{fontSize: '18px'}} />}
                        onClick={handleCloseCounter}
                        loading={isClosingCounter}
                        style={{
                          background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                          borderColor: '#f5576c',
                          height: '50px',
                          minWidth: '180px',
                          fontSize: '15px',
                          fontWeight: '600',
                          borderRadius: '8px',
                          boxShadow: '0 4px 12px rgba(245, 87, 108, 0.5)',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                        onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                      >
                        {isClosingCounter ? 'CLOSING COUNTER...' : 'CLOSE COUNTER'}
                      </Button>
                      <Button 
                        type="primary"
                        size="large"
                        icon={<span style={{fontSize: '18px'}}>🖨️</span>}
                        onClick={generateThermalPrint}
                        style={{
                          background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
                          borderColor: '#a8edea',
                          color: '#6f42c1',
                          height: '50px',
                          minWidth: '180px',
                          fontSize: '15px',
                          fontWeight: '600',
                          borderRadius: '8px',
                          boxShadow: '0 4px 12px rgba(111, 66, 193, 0.3)',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                        onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                      >
                        THERMAL PRINT
                      </Button>
                    </Space>
                  </Card>
                </Col>
              </Row>
            )}

            {counterStatus === 'closed' && (
              <Row gutter={[16, 16]}>
                <Col span={24}>
                  <Card style={{backgroundColor: '#e8f5e9', border: '1px solid #28a745', borderRadius: '8px', textAlign: 'center'}}>
                    <Alert
                      message="Counter has been successfully closed!"
                      type="success"
                      icon={<CheckCircleOutlined />}
                      showIcon
                      style={{marginBottom: '16px'}}
                    />
                    <Space size="middle" wrap>
                      <Button 
                        type="primary"
                        size="large"
                        onClick={() => navigate('/collection/paid-summary')}
                      >
                        Back to Dashboard
                      </Button>
                      <Button 
                        size="large"
                        icon={<FaDownload />}
                        onClick={exportReviewToPDF}
                      >
                        Download Report
                      </Button>
                      <Button 
                        size="large"
                        onClick={generateThermalPrint}
                        style={{backgroundColor: '#6f42c1', borderColor: '#6f42c1', color: 'white'}}
                      >
                        🖨️ Print Receipt
                      </Button>
                    </Space>
                  </Card>
                </Col>
              </Row>
            )}
          </>
        )}

        {/* Review Details Modal - Ant Design */}
        <ReviewModalAntD
          showReviewModal={showReviewModal}
          setShowReviewModal={setShowReviewModal}
          moneyReport={moneyReport}
          counterData={counterData}
          exportReviewToPDF={exportReviewToPDF}
        />

        {/* Expense Modal - Ant Design */}
        <Modal
          title={
            <div style={{color: '#ff9800', fontSize: '18px', fontWeight: '600'}}>
              💰 Add Expense Details
            </div>
          }
          open={showExpenseModal}
          onCancel={() => setShowExpenseModal(false)}
          width={800}
          footer={[
            <Button key="cancel" onClick={() => setShowExpenseModal(false)}>
              Cancel
            </Button>,
            <Button 
              key="save" 
              type="primary" 
              onClick={handleSaveExpenses}
              style={{backgroundColor: '#28a745', borderColor: '#28a745'}}
            >
              Save Expenses
            </Button>
          ]}
        >
          <div 
            style={{marginBottom: '20px'}}
            onKeyDown={(e) => {
              if (e.ctrlKey && e.key === 'Enter') {
                e.preventDefault();
                handleAddExpenseRow();
              }
            }}
          >
            {/* Expense Input Row */}
            <Card style={{backgroundColor: '#fff3e0', marginBottom: '16px'}}>
              <div style={{marginBottom: '16px', fontWeight: '600', color: '#ff9800'}}>
                Enter Expense Details: <span style={{fontSize: '12px', color: '#666', fontWeight: 'normal'}}>(Press Ctrl+Enter to add)</span>
              </div>
              <Row gutter={[16, 16]}>
                <Col span={14}>
                  <Input
                    ref={expenseItemInputRef}
                    placeholder="Item details (e.g., Office supplies, Transportation)"
                    value={currentExpense.item}
                    onChange={(e) => setCurrentExpense(prev => ({...prev, item: e.target.value}))}
                    onPressEnter={(e) => {
                      if (e.ctrlKey) {
                        handleAddExpenseRow();
                      }
                    }}
                  />
                </Col>
                <Col span={7}>
                  <Input
                    type="number"
                    placeholder="Amount"
                    value={currentExpense.amount}
                    onChange={(e) => setCurrentExpense(prev => ({...prev, amount: e.target.value}))}
                    onPressEnter={(e) => {
                      if (e.ctrlKey) {
                        handleAddExpenseRow();
                      }
                    }}
                    prefix="฿"
                  />
                </Col>
                <Col span={3}>
                  <Button 
                    type="primary" 
                    onClick={handleAddExpenseRow}
                    icon={<PlusOutlined />}
                    style={{width: '100%'}}
                  >
                    Add
                  </Button>
                </Col>
              </Row>
            </Card>

            {/* Expense List */}
            {expenseItems.length > 0 && (
              <Card title="Expense List" style={{marginBottom: '16px'}}>
                <table className="table table-hover">
                  <thead style={{backgroundColor: '#f5f5f5'}}>
                    <tr>
                      <th style={{width: '5%'}}>#</th>
                      <th style={{width: '60%'}}>Item Details</th>
                      <th style={{width: '25%', textAlign: 'right'}}>Amount</th>
                      <th style={{width: '10%', textAlign: 'center'}}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {expenseItems.map((expense, index) => (
                      <tr key={expense.id}>
                        <td>{index + 1}</td>
                        <td>{expense.item}</td>
                        <td style={{textAlign: 'right', fontWeight: '600'}}>฿{expense.amount.toLocaleString()}</td>
                        <td style={{textAlign: 'center'}}>
                          <Button 
                            danger 
                            size="small" 
                            icon={<DeleteOutlined />}
                            onClick={() => handleRemoveExpenseRow(expense.id)}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot style={{backgroundColor: '#e8f5e9'}}>
                    <tr>
                      <td colSpan="2" style={{fontWeight: 'bold', fontSize: '16px'}}>Total Expenses</td>
                      <td style={{textAlign: 'right', fontWeight: 'bold', fontSize: '16px', color: '#28a745'}}>
                        ฿{expenseItems.reduce((sum, item) => sum + item.amount, 0).toLocaleString()}
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </Card>
            )}

            {expenseItems.length === 0 && (
              <div style={{textAlign: 'center', padding: '40px', color: '#999'}}>
                <div style={{fontSize: '48px', marginBottom: '16px'}}>📝</div>
                <div>No expenses added yet. Enter item details and amount above to add expenses.</div>
              </div>
            )}
          </div>
        </Modal>

        {/* Original Bootstrap Modal - Commented Out */}
        {false && (
        <Modal 
          show={showReviewModal} 
          onHide={() => setShowReviewModal(false)} 
          size="xl" 
          dialogClassName="modal-90w"
        >
          <BootstrapModal.Header closeButton style={{backgroundColor: 'white', borderBottom: '3px solid #f08934', color: '#f08934', position: 'sticky', top: 0, zIndex: 1050, background: 'white'}}>
            <BootstrapModal.Title style={{color: '#f08934', fontWeight: '600'}}>
              <FaEye className="me-2" />
              Review & Compare Details
            </BootstrapModal.Title>
          </BootstrapModal.Header>
          <BootstrapModal.Body style={{maxHeight: '80vh', overflowY: 'auto', padding: '30px'}}>
            {/* Currency and Coin Denominations in Two Columns */}
            <div className="row mb-4">
              {/* Currency Denominations - Left Column */}
              <div className="col-md-6">
                <h5 style={{color: '#f1842a', borderBottom: '2px solid #f1842a', paddingBottom: '10px'}}>
                  💵 Currency Denominations
                </h5>
                <div className="table-responsive">
                  <table className="table table-bordered table-sm">
                    <thead style={{backgroundColor: 'white', color: '#333'}}>
                      <tr>
                        <th style={{color: 'white', backgroundColor: '#f1842a'}}>Denomination</th>
                        <th style={{color: 'white', backgroundColor: '#f1842a'}}>Qty</th>
                        <th style={{color: 'white', backgroundColor: '#f1842a'}}>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { label: '฿1000', key: 'note1000' },
                        { label: '฿500', key: 'note500' },
                        { label: '฿100', key: 'note100' },
                        { label: '฿50', key: 'note50' },
                        { label: '฿20', key: 'note20' },
                        { label: '฿10', key: 'note10' },
                        { label: '฿5', key: 'note5' },
                        { label: '฿1', key: 'note1' }
                      ].map(denom => (
                        <tr key={denom.key}>
                          <td><strong>{denom.label}</strong></td>
                          <td>{moneyReport.denominations[denom.key]?.qty || 0}</td>
                          <td>฿{(moneyReport.denominations[denom.key]?.total || 0).toLocaleString()}</td>
                        </tr>
                      ))}
                      <tr style={{background: '#d4edda', fontWeight: 'bold'}}>
                        <td colSpan="2"><strong>Total Cash</strong></td>
                        <td>
                          ฿{(
                            Object.entries(moneyReport.denominations)
                              .reduce((sum, [key, value]) => {
                                if (key === 'photo') return sum;
                                return sum + (value.total || 0);
                              }, 0)
                          ).toLocaleString()}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Coin Denominations - Right Column */}
              <div className="col-md-6">
                <h5 style={{color: '#e67e22', borderBottom: '2px solid #e67e22', paddingBottom: '10px'}}>
                  🪙 Coin Denominations
                </h5>
                <div className="table-responsive">
                  <table className="table table-bordered table-sm">
                    <thead style={{backgroundColor: 'white', color: '#333'}}>
                      <tr>
                        <th style={{color: 'white', backgroundColor: '#e67e22'}}>Coin</th>
                        <th style={{color: 'white', backgroundColor: '#e67e22'}}>Qty</th>
                        <th style={{color: 'white', backgroundColor: '#e67e22'}}>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { label: '฿10', key: 'coin10' },
                        { label: '฿5', key: 'coin5' },
                        { label: '฿2', key: 'coin2' },
                        { label: '฿1', key: 'coin1' }
                      ].map(coin => (
                        <tr key={coin.key}>
                          <td><strong>{coin.label}</strong></td>
                          <td>{moneyReport.coins[coin.key]?.qty || 0}</td>
                          <td>฿{(moneyReport.coins[coin.key]?.total || 0).toLocaleString()}</td>
                        </tr>
                      ))}
                      <tr style={{background: '#ffe0b2', fontWeight: 'bold'}}>
                        <td colSpan="2"><strong>Total Coins</strong></td>
                        <td>
                          ฿{(
                            Object.entries(moneyReport.coins)
                              .reduce((sum, [key, value]) => {
                                return sum + (value.total || 0);
                              }, 0)
                          ).toLocaleString()}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Photo/Other Summary */}
              <div className="col-md-6">
                <h5 style={{color: '#f39c12', borderBottom: '2px solid #f39c12', paddingBottom: '10px'}}>
                  📸 Photo/Other Amount
                </h5>
                <div className="table-responsive">
                  <table className="table table-bordered">
                    <thead style={{backgroundColor: 'white', color: '#333'}}>
                      <tr>
                        <th style={{color: 'white', backgroundColor: '#f39c12'}}>Description</th>
                        <th style={{color: 'white', backgroundColor: '#f39c12'}}>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr style={{background: '#fff3cd'}}>
                        <td><strong>Photo/Other</strong></td>
                        <td>฿{(moneyReport.denominations.photo || 0).toLocaleString()}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Payment Methods Comparison */}
            <div className="mb-4">
              <h5 style={{color: '#f08934', borderBottom: '2px solid #f08934', paddingBottom: '10px'}}>
                💳 Payment Methods - Manual vs System
              </h5>
              <div className="table-responsive">
                <table className="table table-bordered">
                  <thead style={{backgroundColor: 'white'}}>
                    <tr>
                      <th style={{color: 'white', backgroundColor: '#f08934'}}>Item</th>
                      <th style={{color: 'white', backgroundColor: '#f08934'}}>Manual Entry</th>
                      <th style={{color: 'white', backgroundColor: '#f08934'}}>Expenses</th>
                      <th style={{color: 'white', backgroundColor: '#f08934'}}>System Calculated</th>
                      <th style={{color: 'white', backgroundColor: '#f08934'}}>Difference</th>
                      <th style={{color: 'white', backgroundColor: '#f08934'}}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td><strong>Cash in System</strong></td>
                      <td>฿{(
                        Object.entries(moneyReport.denominations)
                          .reduce((sum, [key, value]) => {
                            if (key === 'photo') return sum; // Skip photo, don't add it
                            return sum + (value.total || 0);
                          }, 0)
                      ).toLocaleString()}</td>
                      <td>฿{(moneyReport.expenses || 0).toLocaleString()}</td>
                      <td>฿{(counterData.paymentBreakdown.cash.amount || 0).toLocaleString()}</td>
                      <td style={{
                        color: Math.abs(((Object.entries(moneyReport.denominations)
                          .reduce((sum, [key, value]) => {
                            if (key === 'photo') return sum; // Skip photo, don't add it
                            return sum + (value.total || 0);
                          }, 0)) + (moneyReport.expenses || 0)) - (counterData.paymentBreakdown.cash.amount || 0)) === 0 ? '#28a745' : '#dc3545',
                        fontWeight: 'bold'
                      }}>
                        ฿{Math.abs(((Object.entries(moneyReport.denominations)
                          .reduce((sum, [key, value]) => {
                            if (key === 'photo') return sum; // Skip photo, don't add it
                            return sum + (value.total || 0);
                          }, 0)) + (moneyReport.expenses || 0)) - (counterData.paymentBreakdown.cash.amount || 0)).toLocaleString()}
                      </td>
                      <td>
                        {Math.abs(((Object.entries(moneyReport.denominations)
                          .reduce((sum, [key, value]) => {
                            if (key === 'photo') return sum; // Skip photo, don't add it
                            return sum + (value.total || 0);
                          }, 0)) + (moneyReport.expenses || 0)) - (counterData.paymentBreakdown.cash.amount || 0)) === 0 ? 
                          <span className="badge bg-success">✓ Match</span> : 
                          <span className="badge bg-danger">✗ Mismatch</span>
                        }
                      </td>
                    </tr>
                    <tr>
                      <td><strong>Credit Card</strong></td>
                      <td>฿{(moneyReport.creditCard || 0).toLocaleString()}</td>
                      <td>฿{(moneyReport.cashOutCard || 0).toLocaleString()}</td>
                      <td>฿{(counterData.paymentBreakdown.card.amount || 0).toLocaleString()}</td>
                      <td style={{
                        color: Math.abs(((moneyReport.creditCard || 0) + (moneyReport.cashOutCard || 0)) - (counterData.paymentBreakdown.card.amount || 0)) === 0 ? '#28a745' : '#dc3545',
                        fontWeight: 'bold'
                      }}>
                        ฿{Math.abs(((moneyReport.creditCard || 0) + (moneyReport.cashOutCard || 0)) - (counterData.paymentBreakdown.card.amount || 0)).toLocaleString()}
                      </td>
                      <td>
                        {Math.abs(((moneyReport.creditCard || 0) + (moneyReport.cashOutCard || 0)) - (counterData.paymentBreakdown.card.amount || 0)) === 0 ? 
                          <span className="badge bg-success">✓ Match</span> : 
                          <span className="badge bg-danger">✗ Mismatch</span>
                        }
                      </td>
                    </tr>
                    <tr>
                      <td><strong>Transfer</strong></td>
                      <td>฿{(moneyReport.transfer || 0).toLocaleString()}</td>
                      <td>฿{(moneyReport.cashOutTE || 0).toLocaleString()}</td>
                      <td>฿{(counterData.paymentBreakdown.btransfer.amount || 0).toLocaleString()}</td>
                      <td style={{
                        color: Math.abs(((moneyReport.transfer || 0) + (moneyReport.cashOutTE || 0)) - (counterData.paymentBreakdown.btransfer.amount || 0)) === 0 ? '#28a745' : '#dc3545',
                        fontWeight: 'bold'
                      }}>
                        ฿{Math.abs(((moneyReport.transfer || 0) + (moneyReport.cashOutTE || 0)) - (counterData.paymentBreakdown.btransfer.amount || 0)).toLocaleString()}
                      </td>
                      <td>
                        {Math.abs(((moneyReport.transfer || 0) + (moneyReport.cashOutTE || 0)) - (counterData.paymentBreakdown.btransfer.amount || 0)) === 0 ? 
                          <span className="badge bg-success">✓ Match</span> : 
                          <span className="badge bg-danger">✗ Mismatch</span>
                        }
                      </td>
                    </tr>
                    <tr style={{background: '#4caf50', color: 'white', fontWeight: 'bold'}}>
                      <td><strong>GRAND TOTAL</strong></td>
                      <td>฿{(
                        (Object.entries(moneyReport.denominations)
                          .reduce((sum, [key, value]) => {
                            if (key === 'photo') return sum;
                            return sum + (value.total || 0);
                          }, 0)) +
                        (moneyReport.creditCard || 0) +
                        (moneyReport.transfer || 0)
                      ).toLocaleString()}</td>
                      <td>฿{(
                        (moneyReport.expenses || 0) +
                        (moneyReport.cashOutCard || 0) +
                        (moneyReport.cashOutTE || 0)
                      ).toLocaleString()}</td>
                      <td>฿{(
                        (counterData.paymentBreakdown.cash.amount || 0) +
                        (counterData.paymentBreakdown.card.amount || 0) +
                        (counterData.paymentBreakdown.btransfer.amount || 0)
                      ).toLocaleString()}</td>
                      <td style={{fontWeight: 'bold'}}>฿{(
                        Math.abs(
                          (
                            (Object.entries(moneyReport.denominations)
                              .reduce((sum, [key, value]) => {
                                if (key === 'photo') return sum;
                                return sum + (value.total || 0);
                              }, 0)) +
                            (moneyReport.expenses || 0) +
                            (moneyReport.creditCard || 0) +
                            (moneyReport.cashOutCard || 0) +
                            (moneyReport.transfer || 0) +
                            (moneyReport.cashOutTE || 0)
                          ) -
                          (
                            (counterData.paymentBreakdown.cash.amount || 0) +
                            (counterData.paymentBreakdown.card.amount || 0) +
                            (counterData.paymentBreakdown.btransfer.amount || 0)
                          )
                        )
                      ).toLocaleString()}</td>
                      <td><span className="badge" style={{backgroundColor: 'white', color: '#4caf50'}}>TOTAL</span></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Financial Summary */}
            <div className="mb-4">
              <h5 style={{color: '#f08934', borderBottom: '2px solid #f08934', paddingBottom: '10px'}}>
                📊 Financial Summary
              </h5>
              <div className="table-responsive">
                <table className="table table-bordered">
                  <thead style={{backgroundColor: 'white'}}>
                    <tr>
                      <th style={{color: 'white', backgroundColor: '#f08934'}}>Item</th>
                      <th style={{color: 'white', backgroundColor: '#f08934'}}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td><strong>Expenses</strong></td>
                      <td>฿{(moneyReport.expenses || 0).toLocaleString()}</td>
                    </tr>
                    <tr>
                      <td><strong>Cash Out - CARD</strong></td>
                      <td>฿{(moneyReport.cashOutCard || 0).toLocaleString()}</td>
                    </tr>
                    <tr>
                      <td><strong>Cash Out - Transfer</strong></td>
                      <td>฿{(moneyReport.cashOutTE || 0).toLocaleString()}</td>
                    </tr>
                    <tr>
                      <td><strong>Amount Received Today</strong></td>
                      <td>฿{(moneyReport.amountReceivedToday || 0).toLocaleString()}</td>
                    </tr>
                    <tr style={{background: '#e8f5e9'}}>
                      <td><strong>Total Coins Amount</strong></td>
                      <td>฿{(
                        Object.entries(moneyReport.coins)
                          .reduce((sum, [key, value]) => {
                            return sum + (value.total || 0);
                          }, 0)
                      ).toLocaleString()}</td>
                    </tr>
                    <tr style={{background: '#e8f5e9'}}>
                      <td><strong>Total Manual Cash Amount</strong></td>
                      <td>฿{(
                        Object.entries(moneyReport.denominations)
                          .reduce((sum, [key, value]) => {
                            if (key === 'photo') return sum;
                            return sum + (value.total || 0);
                          }, 0)
                      ).toLocaleString()}</td>
                    </tr>
                    <tr style={{background: '#e8f5e9'}}>
                      <td><strong>Total Photo/Other Amount</strong></td>
                      <td>฿{(moneyReport.denominations.photo || 0).toLocaleString()}</td>
                    </tr>
                    <tr style={{background: '#4caf50', color: 'white'}}>
                      <td><strong>GRAND TOTAL</strong></td>
                      <td><strong>฿{(
                        Object.entries(moneyReport.coins)
                          .reduce((sum, [key, value]) => {
                            return sum + (value.total || 0);
                          }, 0) +
                        Object.entries(moneyReport.denominations)
                          .reduce((sum, [key, value]) => {
                            if (key === 'photo') return sum;
                            return sum + (value.total || 0);
                          }, 0) +
                        (moneyReport.denominations.photo || 0)
                      ).toLocaleString()}</strong></td>
                    </tr>
                    <tr style={{background: '#fff3e0'}}>
                      <td><strong>Pending Bills (System)</strong></td>
                      <td>฿{(counterData.summary.pendingBills || 0).toLocaleString()}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Grand Totals */}
            <div className="mb-3">
              <h5 style={{color: '#f08934', borderBottom: '2px solid #f08934', paddingBottom: '10px'}}>
                💎 Grand Totals
              </h5>
              <div className="row">
                <div className="col-md-6">
                  <div style={{
                    background: 'linear-gradient(135deg, #D0FFFBFF, #BAEECEFF)',
                    borderRadius: '10px',
                    padding: '20px',
                    color: 'white',
                    textAlign: 'center'
                  }}>
                    <small style={{fontSize: '0.9rem'}}>Manual Cash Count</small>
                    <h3 style={{fontWeight: 'bold', marginTop: '10px'}}>
                      ฿{(
                        Object.entries(moneyReport.denominations)
                          .reduce((sum, [key, value]) => {
                            if (key === 'photo') return sum; // Skip photo, don't add it
                            return sum + (value.total || 0);
                          }, 0)
                      ).toLocaleString()}
                    </h3>
                  </div>
                </div>
                <div className="col-md-6">
                  <div style={{
                    background: 'linear-gradient(135deg, #4caf50, #81c784)',
                    borderRadius: '10px',
                    padding: '20px',
                    color: 'white',
                    textAlign: 'center'
                  }}>
                    <small style={{fontSize: '0.9rem'}}>Grand Total (Cash + Coins + Photo)</small>
                    <h3 style={{fontWeight: 'bold', marginTop: '10px'}}>
                      ฿{(
                        Object.entries(moneyReport.coins)
                          .reduce((sum, [key, value]) => {
                            return sum + (value.total || 0);
                          }, 0) +
                        Object.entries(moneyReport.denominations)
                          .reduce((sum, [key, value]) => {
                            if (key === 'photo') return sum;
                            return sum + (value.total || 0);
                          }, 0) +
                        (moneyReport.denominations.photo || 0)
                      ).toLocaleString()}
                    </h3>
                  </div>
                </div>
              </div>
            </div>

            {/* Summary Alert */}
            <div className="alert alert-info" role="alert">
              <strong>ℹ️ Note:</strong> Please review all entries carefully before closing the counter. 
              Any mismatches between manual entry and system data should be investigated.
            </div>
          </BootstrapModal.Body>
        </Modal>
        )}
        {/* End of old disabled Bootstrap Modal code */}


        {/* Floating Navigation Modal */}
        <FloatingNavModal currentPage="counter-close" />
      </div>
    </div>
  );
};

// Wrap CounterClose with CloseDateProvider
const CounterCloseWithProvider = () => {
  return (
    <CloseDateProvider>
      {({ nextCloseDate, loading: dateLoading, error: dateError }) => (
        <CounterCloseComponent 
          nextCloseDate={nextCloseDate}
          dateLoading={dateLoading}
          dateError={dateError}
        />
      )}
    </CloseDateProvider>
  );
};

// Rename original component
const CounterCloseComponent = CounterClose;

export default CounterCloseWithProvider;


